import { Injectable } from '@angular/core';
import { TranslateLoader, TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';

// 支持的语言：英文 / 德语 / 台湾繁体中文。
export const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'zh-TW', label: '繁體中文' },
] as const;

export type LangCode = (typeof LANGS)[number]['code'];
export const DEFAULT_LANG: LangCode = 'en';
const LS_KEY = 'lang';

// 全量翻译字典（运行时切换，无需 HTTP；随 JS 打包）。键用点号分组。
export const TRANSLATIONS: Record<LangCode, Record<string, any>> = {
  en: {
    register: {
      title: 'Complete Registration',
      subtitle: 'Complete registration to activate your account',
      checking: 'Verifying invitation…',
      contactUs: 'Please contact us for a new invitation.',
      backToLogin: 'Back to sign in',
      email: 'Email',
      emailLocked: 'This email is set by the invitation and cannot be changed.',
      name: 'Name',
      password: 'Password',
      passwordHint: 'At least 8 characters.',
      confirm: 'Confirm password',
      submitting: 'Registering…',
      submit: 'Complete registration',
      hasAccount: 'Already have an account?',
      reasonMissing: 'Invitation code is missing',
      reasonExpired: 'This invitation has expired',
      reasonUsed: 'This invitation has already been used',
      reasonRevoked: 'This invitation has been revoked',
      reasonInvalid: 'Invalid invitation',
    },
    common: {
      prev: 'Previous',
      next: 'Next',
      search: 'Search...',
      logout: 'Logout',
      viewAll: 'View All',
      menu: 'Menu',
      home: 'Home',
      console: 'Console',
      superAdmin: 'Super Admin',
      adminUser: 'Admin User',
      id: 'ID',
      user: 'User',
      amount: 'Amount',
      status: 'Status',
      date: 'Date',
      actions: 'Actions',
      language: 'Language',
      save: 'Save',
      cancel: 'Cancel',
      confirm: 'Confirm',
      loading: 'Loading...',
      copiedHint: 'Copied to clipboard',
      dblToCopy: 'Double-click to copy',
      copy: 'Copy',
      fullAddress: 'Full address',
      recvAddress: 'receiving address',
      walletWarn:
        'Only send {{coin}} on {{chain}} to this address. Wrong token or chain may cause permanent loss.',
      searchCountry: 'Search country or code',
      noMatch: 'No match',
      backToLogin: 'Back to login',
      completingLogin: 'Completing sign-in…',
    },
    nav: {
      members: 'Members',
      dashboard: 'Dashboard',
      onboarding: 'Onboarding',
      apikeys: 'API Keys',
      security: 'Security',
      logs: 'Logs',
      accounts: 'Accounts',
      transactions: 'Transactions',
      payees: 'Payees',
      transfer: 'Transfer',
      users: 'Users',
      settings: 'Settings',
      profile: 'Company Profile',
      // 菜单分组标题（不可点）
      group: {
        cards: 'Virtual Cards',
        global: 'Global Account',
        stats: 'Statistics',
        other: 'More',
      },
      // 虚拟卡组占位项
      cards: {
        stored: 'Stored-value Card',
        credit: 'Credit-limit Card',
        txns: 'Transactions',
        risk: 'Risk Control',
        holders: 'Cardholders',
      },
      reports: 'Reports',
      statements: 'Statements',
    },
    // 占位页文案
    placeholder: {
      title: 'Coming Soon',
      desc: 'This feature is under development.',
    },
    // 虚拟卡（储值卡 / 额度卡 / 交易 / 风控）
    pgHolders: {
      colActions: 'Actions',
      edit: 'Edit',
      editTitle: 'Edit Cardholder',
      save: 'Save',
      title: 'Cardholders',
      subtitle: 'Cardholder registry — reuse the same holder across multiple cards without re-entering details.',
      add: 'Add Cardholder',
      empty: 'No cardholders yet.',
      colName: 'Name',
      colMember: 'Bound member',
      colEmail: 'Email',
      colMobile: 'Mobile',
      colCreated: 'Created',
      addTitle: 'Add Cardholder',
      member: 'Bound member',
      memberNone: 'None',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      mobilePrefix: 'Prefix',
      mobile: 'Mobile',
      birthDate: 'Birth date',
      region: 'Card region (alpha-3)',
      countryCode: 'Country (alpha-3)',
      state: 'State',
      city: 'City',
      address: 'Address',
      postcode: 'Postcode',
      submit: 'Add',
      nameRequired: 'First and last name are required.',
    },
    pgCards: {
      pageOf: 'Page {{page}} of {{total}}',
      holderReuse: {
        new: 'New cardholder',
        existing: 'Reuse existing',
        hint: 'Reuse an existing cardholder and issue a new card on one of their available BINs. No KYC needed.',
        pickHolder: 'Select cardholder',
        bin: 'Card BIN',
        pickBin: 'Select BIN',
        pickCurrency: 'Select currency',
        network: 'Card network',
        pickNetwork: 'Select card network',
        sharedHint: 'Issue a card for an existing cardholder. If the cardholder already has a master card, the new card is a shared card drawing from the master balance; otherwise this card becomes their master.',
        belowMin: 'Amount is below the minimum recharge {{min}}.',
        aboveMax: 'Amount exceeds the maximum recharge {{max}}.',
        limit: 'Amount (shared limit / first load)',
      },
      sub: {
        primary: 'Primary card',
        sub: 'Sub-card',
        hint: 'Sub-card inherits the cardholder, currency and card BIN of the primary card; it spends from the primary balance up to its own limit.',
        primaryCard: 'Primary card',
        pickPrimary: 'Select primary card',
        limit: 'Sub-card limit',
        badge: 'Sub',
      },
      loadFailed: 'Failed to load cards.',
      submitFailed: 'Operation failed. Please try again.',
      none: 'No cards yet.',
      holder: 'Cardholder',
      card: 'Card',
      currency: 'Currency',
      balance: 'Balance',
      available: 'Available',
      creditLimit: 'Credit limit',
      used: 'Used',
      status: 'Status',
      actions: 'Actions',
      created: 'Created',
      amount: 'Amount',
      type_stored_value: 'Stored-value',
      type_credit_limit: 'Credit-limit',
      status_active: 'Active',
      status_frozen: 'Frozen',
      status_closed: 'Closed',
      freeze: 'Freeze',
      unfreeze: 'Unfreeze',
      close: 'Close card',
      confirmClose: 'Close this card? This cannot be undone.',
      confirmCloseYes: 'Confirm close',
      cancel: 'Cancel',
      create: 'Create',
      creating: 'Creating…',
      holderNameLabel: 'Cardholder name',
      currencyLabel: 'Currency',
      unlimited: 'Unlimited',
      reveal: {
        action: 'Show card number',
        title: 'Card details',
        cardNumber: 'Card number',
        cvv: 'CVV',
        expiry: 'Expiry (YY/MM)',
        holder: 'Cardholder',
        warning: 'Sensitive — do not share or store. Closes when you dismiss.',
        close: 'Done',
        failed: 'Failed to load card details.',
      },
      amountHint: 'Enter amount in major units (e.g. 100.00).',
      kyc: {
        section: 'Cardholder & card details',
        cardBin: 'Card BIN ID',
        amount: 'Initial amount',
        allowOnline: 'Allow online transactions',
        firstName: 'First name',
        lastName: 'Last name',
        mobilePrefix: 'Mobile prefix',
        mobile: 'Mobile',
        email: 'Email',
        birthDate: 'Date of birth',
        region: 'Card region (ISO-3)',
        country: 'Billing country (ISO-3)',
        state: 'State/Province',
        city: 'City',
        address: 'Address',
        postcode: 'Postcode',
      },
      stored: {
        subtitle: 'Prepaid balance cards — top up and spend within the loaded balance.',
        addCard: 'New stored-value card',
        closeForm: 'Close form',
        newCardTitle: 'New stored-value card',
        topUp: 'Top up',
        topUpTitle: 'Top up card',
        amountLabel: 'Top-up amount',
        confirmTopUp: 'Confirm top-up',
        emptyHint: 'Create a stored-value card to load a prepaid balance.',
      },
      credit: {
        subtitle: 'Credit-limit cards — spend up to the approved credit line.',
        addCard: 'New credit-limit card',
        closeForm: 'Close form',
        newCardTitle: 'New credit-limit card',
        creditLimitLabel: 'Credit limit',
        setLimit: 'Adjust limit',
        setLimitTitle: 'Adjust credit limit',
        newLimitLabel: 'New credit limit',
        newLimitHint: 'Cannot be lower than the amount already used.',
        confirmSetLimit: 'Confirm',
        emptyHint: 'Create a credit-limit card to grant a credit line.',
      },
      txns: {
        subtitle: 'Transaction history for a selected card.',
        selectCard: 'Card',
        selectPlaceholder: 'Select a card…',
        noCard: 'No cards available. Create a card first.',
        empty: 'No transactions for this card.',
        colTime: 'Time',
        colType: 'Type',
        colAmount: 'Amount',
        colMemo: 'Memo',
        colStatus: 'Status',
        type_spend: 'Spend',
        type_topup: 'Top-up',
        type_refund: 'Refund',
      },
      risk: {
        subtitle: 'Per-card risk controls — transaction limits and online usage.',
        selectCard: 'Card',
        selectPlaceholder: 'Select a card…',
        noCard: 'No cards available. Create a card first.',
        perTxnLabel: 'Per-transaction limit',
        dailyLabel: 'Daily limit',
        monthlyLabel: 'Monthly limit',
        limitHint: '0 means no limit.',
        allowOnline: 'Allow online transactions',
        save: 'Save rules',
        saving: 'Saving…',
        saved: 'Risk rules saved.',
        loadFailed: 'Failed to load risk rules.',
      },
    },
    enterprise: {
      membersTitle: 'Enterprise Members',
      invite: 'Invite member',
      inviteHint:
        'They will be able to sign in and manage this enterprise. Only the Records Administrator can invite.',
      transfer: 'Transfer enterprise',
      transferConfirm: 'Send transfer request',
      transferTo: 'Transfer to (email)',
      transferHint:
        'The recipient must confirm via a one-time link we email them. Nothing changes until they accept.',
      transferWarn:
        'After they accept, they become the Records Administrator and you are demoted to Administrator. This cannot be undone by you alone.',
      transferSent:
        'Transfer request sent. It takes effect only after the recipient confirms via email.',
      remove: 'Remove',
      noMembers: 'No members yet.',
      noEnterprise: 'No enterprise selected',
      colName: 'Name',
      colEmail: 'Email',
      colRole: 'Role',
      colStatus: 'Status',
      colInvitedBy: 'Invited by',
      colJoinedAt: 'Joined',
      colActions: 'Actions',
      statusActive: 'Active',
      statusPending: 'Invited',
      roleOwner: 'Records Administrator',
      roleAdmin: 'Administrator',
      roleViewer: 'Viewer',
      roleOwnerDesc:
        'the sole person accountable for this enterprise; can manage members and transfer the enterprise',
      roleAdminDesc:
        'can operate the business (profile, API keys, funds) but cannot change members or ownership',
      roleViewerDesc: 'read-only',
      acceptTitle: 'Accept enterprise transfer',
      acceptConfirm: 'Accept and become Records Administrator',
      acceptHint:
        'By accepting, you become the Records Administrator of this enterprise, with access to its funds and KYB records — and the compliance responsibility that comes with them.',
      acceptDone: 'Transfer complete',
      acceptDoneHint: 'You are now the Records Administrator of this enterprise.',
      acceptGoDashboard: 'Go to dashboard',
      acceptNoToken:
        'This link is missing its confirmation token. Please use the link from the email.',
      switch: 'Switch enterprise',
      myEnterprises: 'My Enterprises',
      current: 'Enterprise',
      none: 'No enterprise',
      empty: 'No enterprises yet.',
      create: 'Create new enterprise',
      resume: 'Continue',
    },
    appStatus: {
      draft: 'Draft',
      submitted: 'Submitted',
      kyb_in_review: 'Under Review',
      corporation_in_review: 'Under Review',
      compliance_review: 'Compliance Review',
      info_requested: 'Info Requested',
      approved: 'Approved',
      rejected: 'Rejected',
      suspended: 'Suspended',
      invited: 'Invited',
      unknown: '—',
    },
    verifStatus: {
      verified: 'Verified',
      pending: 'Pending',
      failed: 'Failed',
      unverified: 'Not verified',
    },
    bizType: {
      0: 'Unspecified',
      1: 'Sole Proprietor',
      2: 'Partnership',
      3: 'LLC',
      4: 'Corporation',
      5: 'Non-profit',
      6: 'Government',
      7: 'Listed Company',
    },
    docState: {
      required: 'Required',
      uploaded: 'Uploaded',
      under_review: 'Under Review',
      verified: 'Verified',
      rejected: 'Rejected',
      expired: 'Expired',
    },
    profile: {
      title: 'Company Profile',
      subtitle: 'Read-only view of your completed onboarding profile.',
      loadError: 'Failed to load profile.',
      noApp: 'No enterprise profile yet.',
      noAppHint: 'Complete business onboarding to create one.',
      company: 'Company Information',
      legalName: 'Legal Name',
      businessType: 'Business Type',
      incCountry: 'Incorporation Country',
      incState: 'State / Province',
      incDate: 'Incorporation Date',
      regNumber: 'Registration No.',
      industry: 'Industry',
      mcc: 'MCC',
      website: 'Website',
      phone: 'Phone',
      supportEmail: 'Support Email',
      processingCountries: 'Processing Countries',
      productDesc: 'Business Description',
      tax: 'Tax Identifiers',
      ein: 'EIN',
      vat: 'VAT No.',
      uscc: 'Unified Social Credit Code',
      brn: 'Business Registration No.',
      otherTax: 'Other Tax ID',
      addresses: 'Addresses',
      registeredAddr: 'Registered Address',
      operatingAddr: 'Operating Address',
      volume: 'Estimated Volume',
      monthlyMin: 'Monthly Min',
      monthlyMax: 'Monthly Max',
      avgTxn: 'Avg Txn',
      maxTxn: 'Max Txn',
      people: 'Beneficial Owners & Key People',
      noOwners: 'No beneficial owners declared (exempt).',
      pTitle: 'Title',
      nationality: 'Nationality',
      dob: 'DOB',
      email: 'Email',
      roleLegalRep: 'Legal Rep',
      roleDirector: 'Director',
      roleUbo: 'Beneficial Owner',
      roleRep: 'Representative',
      documents: 'Documents',
      noDocuments: 'No documents uploaded.',
      appId: 'Application ID',
      verifDim: {
        legalEntity: 'Legal Entity',
        taxId: 'Tax ID',
        address: 'Address',
        documents: 'Documents',
        aml: 'AML',
        sanctions: 'Sanctions',
      },
    },
    activity: {
      title: 'Business Activity',
      notProvisioned:
        'This enterprise has no account yet — activity appears after account opening.',
      balance: 'Balance',
      transactions: 'Recent Transactions',
      noTxns: 'No transactions yet.',
      time: 'Time',
      type: 'Type',
      account: 'Account',
      amount: 'Amount',
    },
    settings: {
      subtitle: 'Manage your enterprise profile and view business activity.',
      tabProfile: 'Company Profile',
      tabActivity: 'Business Activity',
    },
    accountsPage: { subtitle: 'Accounts of the current enterprise.' },
    // 后端错误码（Kratos reason）→ 本地化文案。前端用 reason 转译，绝不直接展示后端 message。
    accountStatus: {
      active: 'Active',
      pending: 'Under review',
      inactive: 'Inactive',
      returned: 'Returned',
      closed: 'Closed',
      frozen: 'Frozen',
      suspended: 'Suspended',
      returned_info: 'Info requested',
      closed_pending: 'Closing',
    },
    apiErrors: {
      SUBMERCHANT_NOT_EDITABLE:
        'The channel allows edits only while the application is returned for supplementary documents. It is currently under review, so changes cannot be synced yet.',
      SUBMERCHANT_NOT_ACTIVE:
        'The channel is still reviewing your application. The receiving account opens automatically once approved.',
      SUBMERCHANT_NOT_READY: 'Your application is being prepared. Please try again shortly.',
      MATERIALS_REQUIRED: 'Some required documents are missing. Please complete them first.',
      SETTINGS_UNAVAILABLE:
        'A configuration service is temporarily unavailable. Please try again later.',
      PYVIO_INDUSTRY_UNRESOLVED:
        'The selected industry is not yet mapped for the channel. Please contact support.',
    },
    transactionsPage: { subtitle: 'Transaction history of the current enterprise.' },
    usersPage: { subtitle: 'Key people of the current enterprise (directors, beneficial owners).' },
    pgPayees: {
      docSection: 'Business cooperation document',
      docHint: 'Upload a document evidencing the business relationship between this payee and your company (contract, agreement, etc.).',
      docUpload: 'Upload document',
      docUploading: 'Uploading…',
      docRemove: 'Remove',
      uploadFailed: 'Upload failed. Please try again.',
      subtitle: 'Manage payees of the current enterprise.',
      addPayee: 'Add payee',
      closeForm: 'Close',
      empty: 'No payees yet.',
      colName: 'Name',
      colKind: 'Type',
      colCurrency: 'Currency',
      colMethod: 'Method',
      colBank: 'Bank',
      colStatus: 'Status',
      delete: 'Delete',
      confirmDelete: 'Delete this payee?',
      kind: 'Type',
      kind_supplier: 'Supplier',
      kind_own_account: 'Own account',
      holderType: 'Holder type',
      holder_personal: 'Personal',
      holder_company: 'Company',
      paymentMethod: 'Payment method',
      method_local: 'Local',
      method_swift: 'Swift',
      currency: 'Currency',
      remark: 'Remark',
      profileSection: 'Payee details',
      bankSection: 'Bank details',
      legalName: 'Legal name',
      companyNo: 'Company No.',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      phone: 'Phone',
      country: 'Country',
      city: 'City',
      street: 'Street',
      province: 'Province / State',
      postCode: 'Post code',
      accountNo: 'Account No.',
      accountName: 'Account name',
      bankName: 'Bank name',
      swiftCode: 'SWIFT / BIC',
      iban: 'IBAN',
      accountCountry: 'Account country',
      bankAddress: 'Bank address',
      ownAccountProofNote:
        'An own account requires a bank proof document (e.g. a bank statement or account certificate showing the account belongs to you).',
      bankProofUpload: 'Upload bank proof',
      bankProofRequired: 'Please upload the bank proof document first.',
      uploadStillProcessing:
        'The document is still being processed. Please try again in a moment.',
      submit: 'Add payee',
      submitting: 'Saving…',
      submitFailed: 'Failed to save payee.',
    },
    pgTransfer: {
      subtitle: 'Send a transfer or payout from the current enterprise.',
      formTitle: 'Transfer / Payout',
      payee: 'Payee',
      selectPayee: 'Select a payee',
      noPayees: 'No payees yet. Add a payee first.',
      fromAccount: 'From account',
      selectAccount: 'Select an account',
      noAccounts: 'No settlement account available.',
      amount: 'Amount',
      purpose: 'Purpose',
      postscript: 'Postscript',
      fxNeeded: 'Cross-currency ({{origin}} → {{target}}). Lock a rate to continue.',
      lockRate: 'Lock rate',
      relock: 'Refresh rate',
      quoting: 'Locking…',
      rate: 'Rate',
      estReceive: 'Est. received',
      expiresIn: 'Expires in',
      expired: 'Expired',
      submit: 'Submit transfer',
      submitting: 'Submitting…',
      failed: 'Failed to submit transfer.',
      submitted: 'Transfer submitted',
      movementId: 'Movement ID',
      status: 'Status',
      newTransfer: 'New transfer',
    },
    dashboard: {
      title: 'Dashboard',
      overview: 'Overview',
      balances: 'Balances',
      recentActivity: 'Recent Activity',
      noTxns: 'No transactions yet.',
      noAccounts: 'You have no accounts yet. Complete business onboarding to open one.',
      completeOnboarding: 'Complete Onboarding',
      onbApprovedTitle: '✓ Business onboarding approved',
      onbApprovedHint: 'Your settlement account is being provisioned. It will appear here shortly.',
      onbPendingTitle: 'Business onboarding under review',
      onbPendingHint:
        'Company and all required beneficial owners are completing ID&V verification.',
      onbFailedTitle: 'Onboarding was not approved. Please contact compliance or re-submit.',
      onbViewStatus: 'View onboarding status',
      refresh: 'Refresh',
      statAccounts: 'Accounts',
      statBalance: 'Primary Balance',
      statActive: 'Active',
      statOnboarding: 'Onboarding',
      statRecentTxns: 'Recent Txns',
      quickActions: 'Quick Actions',
      qaApiKey: 'Issue API Key',
      qaSecurity: 'Security Settings',
      qaOnboarding: 'Onboarding',
      txnId: 'ID',
      txnType: 'Type',
      txnAmount: 'Amount',
      txnDate: 'Date',
      priceBuy: 'Buy price',
      priceSell: 'Sell price',
      ovTitle: 'Total Balance',
      ovSub: 'Overview of your current funds',
      primaryAccount: 'Primary Account',
      transfer: 'Transfer',
      received: 'Received',
      funding: 'Funding Overview',
      income: 'Income',
      expense: 'Expense',
      mIncome: 'Monthly Income',
      mSpent: 'Total Spent',
      mSaving: 'Saving Rate',
      comingSoon: 'Coming soon',
      orderId: 'Order ID',
      activity: 'Activity',
      price: 'Price',
      status: 'Status',
      statusDone: 'Completed',
      filter: 'Filter',
      search: 'Search',
      totalRevenue: 'Total Revenue',
      range3: '3 Months',
      range6: '6 Months',
      range12: '1 Year',
      convertTitle: 'Convert',
      pair: 'Pair',
      side: 'Side',
      youPay: 'You pay',
      youReceive: 'You receive',
      sell: 'Sell',
      buy: 'Buy',
      amount: 'Amount',
      getQuote: 'Get Quote',
      rate: 'Rate',
      fee: 'Fee',
      youGet: 'You get',
      convert: 'Convert now',
      convertOk: 'Converted',
    },
    login: {
      failed: 'Incorrect email or password.',
      mfaInvalid: 'Invalid verification code.',
      locked: 'Account temporarily locked. Try again later.',
      passkeyUnavailable: 'Passkey sign-in is unavailable. Use your password.',
      tagline: 'Future-Ready Finance Console',
      welcomeBack: 'Welcome Back',
      signInToContinue: 'Please sign in to continue',
      usernameOrEmail: 'Username or Email',
      password: 'Password',
      forgotPassword: 'Forgot Password?',
      login: 'LOGIN',
      loggingIn: 'LOGGING IN…',
      orUse: 'or',
      passkeyLogin: 'Sign in with Passkey',
      verifying: 'Verifying…',
      twoFactor: 'Two-Factor Authentication',
      enterTotp: 'Enter the 6-digit code from your authenticator app',
      totpPlaceholder: '6-digit code / recovery code',
      verifyAndLogin: 'Verify & Login',
      back: 'Back',
    },
    logs: {
      title: 'Activity Log',
      subtitle: 'Review the login and security activity on your account.',
      operation: 'Activity',
      source: 'Source',
      device: 'Device',
      ipAddr: 'IP Address',
      location: 'Location',
      requestId: 'Request ID',
      result: 'Status',
      time: 'Time',
      noRecords: 'No activity records.',
      devApp: 'App',
      devMobile: 'Mobile',
      devTablet: 'Tablet',
      devDesktop: 'Desktop',
      devApi: 'API',
      devUnknown: 'Unknown',
      geoLan: 'Local',
      prev: 'Previous',
      next: 'Next',
      success: 'Success',
      failed: 'Failed',
      pageInfo: '{{total}} total · Page {{page}}/{{pages}}',
      actLogin: 'Login',
      actRegister: 'Register',
      actRefresh: 'Token refresh',
      actTotpEnroll: 'Bind authenticator',
      actTotpDisable: 'Disable authenticator',
      actPasskeyRegister: 'Add Passkey',
      actPasskeyDelete: 'Delete Passkey',
      actStepup: 'Transaction step-up',
      actApikeyCreate: 'Create API Key',
      actApikeyRevoke: 'Revoke API Key',
    },
    onboarding: {
      title: 'Business Onboarding',
      intro:
        'Submit your company details to start KYB verification. Data is collected and verified on a third-party (Sumsub) hosted page; once approved your account is opened, after which you can issue AK/SK for server-side calls on the “API Keys” page.',
      contactEmail: 'Contact Email',
      contactPhone: 'Contact Phone',
      country: 'Country / Region of Registration',
      bizType: 'Business Type',
      submit: 'Start Onboarding',
      submitNote:
        'After submission a third-party hosted verification link is generated, where company details, UBO (beneficial owners) and documents are filled in.',
      appId: 'Application ID',
      verifiedMsg:
        '✓ Verification passed, account is being opened. Once done, head to “API Keys” to issue server-side keys.',
      failedMsg: '✕ Verification failed. To appeal or resubmit, please contact compliance support.',
      goVerify: 'Go to verification page',
      verifyNote:
        'After completing the form on the new page, return here — the status refreshes automatically.',
      dimensions: 'Verification Dimensions',
      restart: 'Restart Onboarding',
      restartNote1:
        'This clears the local reference to the current application and returns to the form.',
      restartNote2:
        'Applications already submitted to the backend are unaffected; contact support to look up history.',
      confirmRestart: 'Confirm Restart',
      starting: 'Starting…',
      submitFull: 'Start Onboarding Verification',
      provider: 'Verification provider',
      refresh: 'Refresh',
      refreshing: 'Refreshing…',
      statusVerified: 'Passed',
      statusFailed: 'Failed',
      statusPending: 'Verifying',
      statusWaiting: 'Awaiting',
      biz1: 'Sole Proprietorship',
      biz2: 'Partnership',
      biz3: 'Limited Liability Company (LLC)',
      biz4: 'Joint-Stock Company',
      biz5: 'Non-Profit',
      biz6: 'Government Agency',
      biz7: 'Public Company',
      dimLegalEntity: 'Legal Entity',
      dimTaxId: 'Tax ID',
      dimAddress: 'Registered Address',
      dimDocuments: 'Documents',
      dimAml: 'AML',
      dimSanctions: 'Sanctions Screening',
      expiryNever: 'Never expires',
      expiry30: '30 days',
      expiry90: '90 days',
      expiry1y: '1 year',
      // ── Tab 标签 ──
      tabEntity: 'Entity Info',
      tabBusiness: 'Business Info',
      tabOwnership: 'Ownership',
      tabPeople: 'Key People',
      // ── 回显横幅 ──
      bannerStatus: 'Application No.',
      bannerEcho: ' (data restored below; each item shows its verification status)',
      refreshShort: 'Refresh',
      // ── 企业主体核验徽章 ──
      companyVerifTitle: 'Company Verification',
      // ── 公司基本信息 ──
      sectionCompanyBasics: 'Company Basics',
      legalName: 'Legal Name',
      dba: 'Doing Business As (DBA)',
      regNumber: 'Registration Number',
      regNumberPh: 'Company registration number',
      taxIdPh: 'Tax / registration number (per country of registration)',
      incState: 'State / Province of Incorporation',
      incStatePh: 'Delaware / Hong Kong',
      incDate: 'Date of Incorporation',
      // ── 注册地址 ──
      sectionRegAddress: 'Registered Address',
      line1: 'Address Line 1',
      line2: 'Address Line 2',
      city: 'City',
      state: 'State / Province',
      postal: 'Postal Code',
      addrCountry: 'Country / Region',
      // ── 经营地址 ──
      sectionOpAddress: 'Operating Address',
      sameAsReg: 'Same as registered address',
      opSameHint: 'Operating address is the same as the registered address.',
      // ── 联系方式与业务 ──
      sectionContactBiz: 'Contact & Business',
      companyPhone: 'Company Phone',
      website: 'Website',
      supportEmail: 'Support Email',
      mcc: 'MCC (Merchant Category Code)',
      industry: 'Industry / Sector',
      industryPh: 'Fintech / cross-border payments',
      industryCat: 'Industry Category',
      industrySub: 'Industry Sub-category',
      industrySelect: 'Please select',
      ind: {
        financial_services: 'Financial Services',
        software_tech: 'Software & Technology',
        ecommerce_retail: 'E-commerce & Retail',
        professional_services: 'Professional Services',
        trade_logistics: 'Trade & Logistics',
        manufacturing: 'Manufacturing & Industrial',
        media_content: 'Media & Content',
        health_life: 'Health & Life Sciences',
        travel_mobility: 'Travel & Mobility',
        education: 'Education',
        realestate_construction: 'Real Estate & Construction',
        other: 'Other',
        payments_acquiring: 'Payments / Acquiring',
        remittance_fx: 'Remittance / FX',
        ewallet_emi: 'E-wallet / EMI',
        lending_credit: 'Lending / Credit',
        wealth_asset: 'Wealth / Asset Management',
        crypto_vasp: 'Crypto / VASP',
        insurtech: 'InsurTech',
        saas: 'SaaS / Enterprise Software',
        cloud_infra: 'Cloud / Infrastructure',
        ai_data: 'AI / Data',
        cybersecurity: 'Cybersecurity',
        blockchain_web3: 'Blockchain / Web3',
        devtools: 'Developer Tools',
        b2b_ecommerce: 'B2B E-commerce',
        marketplace: 'Marketplace',
        wholesale: 'Wholesale / Distribution',
        dtc_brand: 'DTC Brand',
        retailtech: 'Retail Tech',
        consulting: 'Consulting',
        legal: 'Legal Services',
        accounting_audit: 'Accounting / Audit',
        marketing_ad: 'Marketing / Advertising',
        hr_recruiting: 'HR / Recruiting',
        import_export: 'Import / Export',
        freight: 'Freight / Logistics',
        supply_chain: 'Supply Chain',
        warehousing: 'Warehousing / Fulfillment',
        customs: 'Customs / Clearance',
        electronics: 'Electronics / Hardware',
        industrial_equipment: 'Industrial Equipment',
        consumer_goods: 'Consumer Goods',
        automotive: 'Automotive / Parts',
        chemicals: 'Chemicals / Materials',
        digital_media: 'Digital Media / Publishing',
        gaming: 'Gaming',
        adtech: 'AdTech',
        creator_economy: 'Creator Economy',
        digital_health: 'Digital Health',
        medical_devices: 'Medical Devices',
        biotech_pharma: 'Biotech / Pharma',
        health_services: 'Health Services',
        ota_travel: 'Travel / OTA',
        hospitality: 'Hospitality / Lodging',
        ride_mobility: 'Ride / Mobility',
        ticketing: 'Ticketing',
        edtech: 'EdTech',
        training_cert: 'Training / Certification',
        edu_services: 'Education Services',
        proptech: 'PropTech',
        construction: 'Construction / Engineering',
        property_mgmt: 'Property Management',
        other_general: 'Other',
      },
      productDesc: 'Product / Service Description',
      productDescPh: 'Briefly describe your product, target customers and fund flow',
      // ── 预估流水 ──
      sectionVolume: 'Estimated Business Volume',
      currency: 'Currency',
      volMin: 'Monthly Volume (min)',
      volMax: 'Monthly Volume (max)',
      volAvg: 'Average Transaction',
      volHigh: 'Highest Transaction',
      // ── 上市公司 ──
      listedLabel: 'This is a public company (listing may exempt UBO look-through)',
      exchange: 'Exchange',
      ticker: 'Ticker',
      // ── 股权结构 ──
      ownTitle: 'Ownership Structure (multi-layer)',
      ownIntro:
        'Your company sits at the top; click “+ shareholder” on a node to expand the ownership chain downward (top-down org-chart style). Fill the direct ownership % on each branch. Multi-layer look-through is supported; the system identifies UBOs along each chain automatically.',
      ownHint:
        'Tip: when there are many layers, hover over any node to highlight its full ownership chain up to “your company”; each layer is color-coded. An individual whose look-through stake reaches 25% is flagged as a UBO.',
      thisCompany: 'Your Company',
      thisCompanyTag: 'Your Company',
      nodePerson: 'Individual',
      nodeEntity: 'Entity',
      nodeNamePh: 'Shareholder name',
      uboBadge: 'UBO · look-through {{pct}}%',
      uboSummaryTitle: 'Ultimate beneficial owners (UBO)',
      uboSummaryHint: 'all look-through individuals; ≥ {{threshold}}% are UBOs requiring ID&V',
      effPct: 'look-through {{pct}}%',
      uboUnnamed: '(unnamed)',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      zoomReset: 'Reset zoom',
      zoomFit: 'Fit to screen',
      addEntityShareholder: 'Add entity shareholder',
      addPersonShareholder: 'Add individual shareholder',
      removeShareholder: 'Remove this shareholder and its upstream owners',
      directSum: 'Direct ownership total {{sum}}%',
      directSumOk: ' ✓',
      directSumBad: ' (should be 100%, cannot submit)',
      noBoDeclare:
        'Declare no beneficial owners (public companies etc. may be exempt from look-through)',
      ownConfirm:
        'I confirm the ownership / beneficial-owner structure above is true and complete, and understand each required person must complete their own ID&V identity verification.',
      // ── 关键人员 ──
      peopleTitle: 'Key People',
      peopleIntroA: 'You must designate at least one ',
      peopleRoleBo: 'beneficial owner',
      peopleIntroB: ', one ',
      peopleRoleLr: 'legal representative',
      peopleIntroC: ' and one ',
      peopleRoleFa: 'file administrator',
      peopleIntroD:
        ' (one person may hold several roles, or roles may be split across people). Beneficial owners reaching the threshold (usually 25%) and all directors / legal representatives / file administrators must complete ID&V verification. ',
      peopleIntroEmail:
        'The file administrator’s email will be used as the contact email for this application.',
      personTag: 'Key Person',
      fromTree: 'From ownership · look-through {{pct}}%',
      idvLabel: 'ID&V {{status}}',
      remove: 'Remove',
      firstName: 'First name',
      lastName: 'Last name',
      dob: 'Date of Birth',
      nationality: 'Nationality',
      personEmail: 'Email (for ID&V invitation)',
      titleLabel: 'Title',
      titlePh: 'CEO / Director',
      ownershipPct: 'Ownership Percent (%)',
      roleBo: 'Beneficial Owner',
      roleDirector: 'Director',
      roleLr: 'Legal Representative',
      roleFa: 'File Administrator',
      addOwner: '+ Add Beneficial Owner / Administrator',
      // ── ID&V 徽章状态 ──
      idvPassed: 'Passed',
      idvFailed: 'Failed',
      idvPending: 'Reviewing',
      idvWaiting: 'Awaiting',
      // ── 操作区 ──
      submitHint:
        'Before submitting, confirm the structure on the “Ownership” tab; information from all tabs is submitted together.',
      saving: 'Saving…',
      saveDraft: 'Save Draft',
      submitting: 'Submitting…',
      submitApply: 'Submit Application & Start Verification',
      // ── 提交后清单 ──
      progressLabel: 'Verification progress: ',
      progressDoneSuffix: ' required item(s) passed',
      checklistTitle: 'ID&V Checklist',
      taskCompany: 'Company Entity',
      taskPerson: 'Related Person',
      taskRequired: 'Required',
      taskHolding: 'Ownership {{pct}}%',
      goVerifyShort: 'Verify',
      checklistFooter:
        'Each required person opens their own link to complete ID&V; once the company entity and all required people pass, the account is opened automatically.',
      // ── 校验/提示文案 ──
      errLegalName: 'Please enter the company legal name',
      errRegNumber: 'Please enter the company registration number',
      errTaxId: 'Please enter {{label}} (tax number required)',
      errIncState: 'Please enter the state / province of incorporation',
      errIncDate: 'Please enter the date of incorporation',
      errRegAddress: 'Please complete the registered address (line 1 / city / state / postal code)',
      errFileAdmin: 'Please designate a file administrator',
      errFileAdminEmail: 'The file administrator needs a valid email (used as the contact email)',
      errLegalRep: 'Please designate a legal representative',
      errBeneficialOwner:
        'Please designate a beneficial owner, or check the “no beneficial owners” declaration',
      errOwnershipSum: '“{{name}}” direct ownership totals {{sum}}%, but should be 100%',
      errOwnershipConfirm:
        'Please confirm the ownership / beneficial-owner structure is true before submitting',
      draftEntityPrefix: 'Please fill in “Entity Info” before saving the draft: ',
      noticeDraftSaved: 'Draft saved',
      errDraftFailed: 'Failed to save draft: ',
      errSubmitFailed: 'Submission failed: ',
      errStatusFailed: 'Failed to query status: ',
      msgFallback: 'Request failed, please try again later',
      // ── 税务号标签 ──
      taxFallback: 'Tax / registration number',
      tax_US: 'EIN (Employer ID Number)',
      tax_CN: 'Unified Social Credit Code',
      tax_DE: 'Tax Number',
      tax_GB: 'Company Number',
      tax_FR: 'Company ID (SIREN)',
      tax_AU: 'Business Number (ABN)',
      tax_SG: 'Entity Number (UEN)',
      tax_HK: 'Business Registration No. (BRN)',
      tax_JP: 'Corporate Number',
      tax_CA: 'Business Number (BN)',
      // ── 证明文件 Tab ──
      tabDocuments: 'Documents',
      docsCompanyTitle: 'Company Documents',
      docsCompanyHint:
        'Upload company supporting documents. Required items must be provided; conditional items are requested only when applicable (e.g. enhanced due diligence).',
      docsPersonTitle: 'Individual Documents',
      docsPersonHint:
        'Upload identity and address documents for each key person (UBO / director / legal representative / file administrator).',
      docsNoPersons:
        'No persons saved yet. Save the application first (Save Draft), then upload individual documents here.',
      docRequired: 'Required',
      docConditional: 'Conditional',
      docUpload: 'Upload',
      docUploading: 'Uploading…',
      docDelete: 'Delete',
      docNeedApp: 'Please save the application first (Save Draft), then upload documents.',
      docUploadedOk: 'Uploaded {{name}}',
      docUploadFailed: 'Upload failed: ',
      docDeleteFailed: 'Delete failed: ',
      docState_required: 'Required',
      docState_uploaded: 'Uploaded',
      docState_under_review: 'Under review',
      docState_verified: 'Verified',
      docState_rejected: 'Rejected',
      docState_expired: 'Expired',
      docTypes: {
        certificate_of_incorporation: 'Certificate of Incorporation',
        articles_of_association: 'Articles of Association / Bylaws',
        proof_of_registered_address: 'Proof of Registered Address (within 3 months)',
        ubo_declaration: 'UBO Declaration',
        business_license: 'Business License',
        bank_statement: 'Bank Statement (within 3 months)',
        financial_statements: 'Financial Statements (EDD)',
        certificate_of_good_standing: 'Certificate of Good Standing',
        board_resolution: 'Board Resolution',
        tax_registration: 'Tax Registration Certificate',
        register_of_directors: 'Register of Directors',
        register_of_shareholders: 'Register of Shareholders / Share Register',
        passport_or_national_id: 'Passport / National ID',
        proof_of_address: 'Proof of Address (within 3 months)',
        power_of_attorney: 'Power of Attorney',
        source_of_wealth_evidence: 'Source of Wealth Evidence (EDD)',
        nnc1_incorporation_form: 'Incorporation Form (NNC1)',
        nar1_annual_return: 'Annual Return (NAR1)',
        significant_controllers_register: 'Significant Controllers Register (SCR)',
        acra_business_profile: 'ACRA Business Profile',
        asic_company_extract: 'ASIC Company Extract',
        confirmation_statement: 'Confirmation Statement (CS01)',
        psc_register: 'PSC Register',
        gesellschafterliste: 'List of Shareholders (Gesellschafterliste)',
        handelsregisterauszug: 'Commercial Register Extract (Handelsregister)',
        b1_annual_return: 'Annual Return (Form B1)',
        company_extract: 'Company Extract',
        particulars_of_shareholding: 'Particulars of Shareholding',
        extrait_kbis: 'Trade Register Extract (Kbis)',
        statuts_articles_of_association: 'Articles of Association (Statuts)',
        beneficial_owners_declaration: 'Beneficial Owners Register (RBE)',
        kvk_business_register_extract: 'KvK Business Register Extract',
        shareholder_register: 'Shareholders Register',
        ubo_register_extract: 'UBO Register Extract',
        commercial_register_extract: 'Commercial Register Extract',
        share_register_aktienregister: 'Share Register (Aktienregister)',
        commercial_register_extract_gmbh_members: 'Commercial Register Extract (GmbH members)',
        registro_mercantil_extract: 'Commercial Registry Extract (Nota Simple)',
        good_standing_certificate: 'Certificate of Good Standing',
        escritura_de_constitucion: 'Deed of Incorporation (Escritura)',
        beneficial_ownership_certificate: 'Certificate of Beneficial Ownership',
        visura_camerale: 'Company Registry Extract (Visura)',
        certificate_of_all_historical_matters: 'Certificate of All Historical Matters',
        shareholder_registry_certificate: 'Shareholder Registry Certificate',
        memorandum_of_association: 'Memorandum of Association (MOA)',
        share_certificate: 'Share Certificate / Register of Members',
        certificate_of_incumbency: 'Certificate of Incumbency',
        board_resolution_appointment: 'Board Resolution (Director Appointment)',
        cap_table: 'Capitalization Table',
        stock_ledger: 'Stock Transfer Ledger',
        operating_agreement: 'LLC Operating Agreement',
        statement_of_information: 'Statement of Information',
        corporate_bylaws: 'Corporate Bylaws',
        corporate_profile_report: 'Corporate Profile Report',
        isc_register: 'Register of Individuals with Significant Control (ISC)',
      },
    },
    security: {
      title: 'Account Security',
      subtitle:
        'Bind two-factor authentication or a Passkey to protect your account and transactions.',
      enrollPrompt1: 'For transaction security, please bind at least one of ',
      enrollPrompt2: ' before continuing.',
      totp: 'Two-Factor (TOTP)',
      totpDesc: 'Use an authenticator app such as Google Authenticator / Authy.',
      bindTotp: 'Bind authenticator code',
      disableTotp: 'Disable two-factor',
      scanStep: '① Scan with your authenticator app, or enter the key manually:',
      confirmStep: '② Enter the 6-digit code from the app to confirm:',
      confirmBind: 'Confirm binding',
      recoveryIntro:
        '✅ Enabled. Please keep the following one-time recovery codes safe (used if you lose your authenticator; shown only once):',
      savedRecovery: 'I have saved them',
      passkey: 'Passkey',
      passkeyDesc:
        'Passwordless login via device biometrics / security key; a strong second factor in itself.',
      addPasskey: 'Add Passkey',
      noPasskey: 'No Passkey added yet.',
      currentCode: 'Current code / recovery code',
      totpCode: '6-digit code',
      deviceAlias: 'Device alias (e.g. My Phone)',
      enabled: 'Enabled',
      disabled: 'Disabled',
      or: 'or',
      totpCodeName: 'TOTP authenticator code',
      unnamedDevice: 'Unnamed device',
      addedOn: 'Added on',
      delete: 'Delete',
    },
    apikeys: {
      title: 'API Keys',
      subtitle:
        'Manage access keys (AK/SK) for server-side calls. Keys are issued via customer → auth; the gateway verifies signatures to allow requests.',
      create: 'Create API Key',
      all: 'All',
      active: 'Active',
      revoked: 'Revoked',
      owner: 'Owner',
      application: 'application',
      merchant: 'merchant',
      refresh: 'Refresh',
      empty: 'No API Keys yet',
      createFirst: 'Create your first API Key',
      name: 'Name',
      accessKey: 'Access Key',
      scopes: 'Scopes',
      createdAt: 'Created',
      lastUsed: 'Last Used',
      expiry: 'Expires',
      none: 'None',
      copied: 'Copied',
      revoke: 'Revoke',
      scopesLabel: 'Scopes',
      validity: 'Validity',
      secretOnce:
        '⚠️ The Secret Key is shown only once here and cannot be viewed after closing — please save it immediately.',
      ak: 'Access Key (AK)',
      sk: 'Secret Key (SK)',
      savedIt: 'I have saved it',
      revokeTitle: 'Revoke API Key',
      revokeConfirm1: 'Confirm revoking “',
      revokeConfirm2: 'This is irreversible; all calls using this key will fail immediately.',
      confirmRevoke: 'Confirm Revoke',
      namePlaceholder: 'e.g. gateway-prod',
      copy: 'Copy',
      owns: 'Owner',
      creating: 'Creating…',
      createShort: 'Create',
      neverUsed: 'Never',
      neverExpire: 'Never',
      createdSuccess: '“{{name}}” created',
    },
  },
  de: {
    register: {
      title: 'Registrierung abschließen',
      subtitle: 'Schließen Sie die Registrierung ab, um Ihr Konto zu aktivieren',
      checking: 'Einladung wird geprüft…',
      contactUs: 'Bitte kontaktieren Sie uns für eine neue Einladung.',
      backToLogin: 'Zurück zur Anmeldung',
      email: 'E-Mail',
      emailLocked:
        'Diese E-Mail ist durch die Einladung festgelegt und kann nicht geändert werden.',
      name: 'Name',
      password: 'Passwort',
      passwordHint: 'Mindestens 8 Zeichen.',
      confirm: 'Passwort bestätigen',
      submitting: 'Registrierung läuft…',
      submit: 'Registrierung abschließen',
      hasAccount: 'Sie haben bereits ein Konto?',
      reasonMissing: 'Einladungscode fehlt',
      reasonExpired: 'Diese Einladung ist abgelaufen',
      reasonUsed: 'Diese Einladung wurde bereits verwendet',
      reasonRevoked: 'Diese Einladung wurde widerrufen',
      reasonInvalid: 'Ungültige Einladung',
    },
    common: {
      prev: 'Zurück',
      next: 'Weiter',
      search: 'Suchen...',
      logout: 'Abmelden',
      viewAll: 'Alle ansehen',
      menu: 'Menü',
      home: 'Start',
      console: 'Konsole',
      superAdmin: 'Super-Admin',
      adminUser: 'Admin-Benutzer',
      id: 'ID',
      user: 'Benutzer',
      amount: 'Betrag',
      status: 'Status',
      date: 'Datum',
      actions: 'Aktionen',
      language: 'Sprache',
      save: 'Speichern',
      cancel: 'Abbrechen',
      confirm: 'Bestätigen',
      loading: 'Wird geladen...',
      copiedHint: 'In die Zwischenablage kopiert',
      dblToCopy: 'Zum Kopieren doppelklicken',
      copy: 'Kopieren',
      fullAddress: 'Vollständige Adresse',
      recvAddress: 'Empfangsadresse',
      walletWarn:
        'Senden Sie nur {{coin}} auf {{chain}} an diese Adresse. Falscher Token oder falsche Chain kann zu dauerhaftem Verlust führen.',
      searchCountry: 'Land oder Vorwahl suchen',
      noMatch: 'Keine Treffer',
      backToLogin: 'Zur Anmeldung',
      completingLogin: 'Anmeldung wird abgeschlossen…',
    },
    nav: {
      members: 'Mitglieder',
      dashboard: 'Dashboard',
      onboarding: 'Registrierung',
      apikeys: 'API-Schlüssel',
      security: 'Sicherheit',
      logs: 'Protokolle',
      accounts: 'Konten',
      transactions: 'Transaktionen',
      payees: 'Empfänger',
      transfer: 'Überweisung',
      users: 'Benutzer',
      settings: 'Einstellungen',
      profile: 'Unternehmensprofil',
      // 菜单分组标题（不可点）
      group: {
        cards: 'Virtuelle Karten',
        global: 'Globales Konto',
        stats: 'Statistik',
        other: 'Mehr',
      },
      // 虚拟卡组占位项
      cards: {
        stored: 'Guthabenkarte',
        credit: 'Kreditkarte',
        txns: 'Transaktionen',
        risk: 'Risikokontrolle',
        holders: 'Karteninhaber',
      },
      reports: 'Berichte',
      statements: 'Kontoauszüge',
    },
    // 占位页文案
    placeholder: {
      title: 'Demnächst verfügbar',
      desc: 'Diese Funktion befindet sich in Entwicklung.',
    },
    pgHolders: {
      colActions: 'Aktionen',
      edit: 'Bearbeiten',
      editTitle: 'Karteninhaber bearbeiten',
      save: 'Speichern',
      title: 'Karteninhaber',
      subtitle: 'Karteninhaber-Register — denselben Inhaber für mehrere Karten wiederverwenden, ohne Daten erneut einzugeben.',
      add: 'Karteninhaber hinzufügen',
      empty: 'Noch keine Karteninhaber.',
      colName: 'Name',
      colMember: 'Zugeordnetes Mitglied',
      colEmail: 'E-Mail',
      colMobile: 'Mobil',
      colCreated: 'Erstellt',
      addTitle: 'Karteninhaber hinzufügen',
      member: 'Zugeordnetes Mitglied',
      memberNone: 'Keins',
      firstName: 'Vorname',
      lastName: 'Nachname',
      email: 'E-Mail',
      mobilePrefix: 'Vorwahl',
      mobile: 'Mobil',
      birthDate: 'Geburtsdatum',
      region: 'Kartenregion (alpha-3)',
      countryCode: 'Land (alpha-3)',
      state: 'Bundesland',
      city: 'Stadt',
      address: 'Adresse',
      postcode: 'PLZ',
      submit: 'Hinzufügen',
      nameRequired: 'Vor- und Nachname sind erforderlich.',
    },
    pgCards: {
      pageOf: 'Seite {{page}} von {{total}}',
      holderReuse: {
        new: 'Neuer Karteninhaber',
        existing: 'Vorhandenen verwenden',
        hint: 'Vorhandenen Karteninhaber wiederverwenden und eine neue Karte auf einer seiner verfügbaren BINs ausgeben. Kein KYC nötig.',
        pickHolder: 'Karteninhaber wählen',
        bin: 'Karten-BIN',
        pickBin: 'BIN wählen',
        pickCurrency: 'Währung wählen',
        network: 'Kartennetzwerk',
        pickNetwork: 'Kartennetzwerk wählen',
        sharedHint: 'Karte für vorhandenen Karteninhaber ausgeben. Hat der Karteninhaber bereits eine Hauptkarte, ist die neue Karte eine Shared-Card auf dem Hauptkartensaldo; sonst wird diese Karte seine Hauptkarte.',
        belowMin: 'Betrag liegt unter der Mindestaufladung {{min}}.',
        aboveMax: 'Betrag überschreitet die maximale Aufladung {{max}}.',
        limit: 'Betrag (Shared-Limit / Erstaufladung)',
      },
      sub: {
        primary: 'Hauptkarte',
        sub: 'Unterkarte',
        hint: 'Die Unterkarte erbt Karteninhaber, Währung und Karten-BIN der Hauptkarte; sie gibt aus dem Guthaben der Hauptkarte bis zu ihrem eigenen Limit aus.',
        primaryCard: 'Hauptkarte',
        pickPrimary: 'Hauptkarte wählen',
        limit: 'Unterkartenlimit',
        badge: 'Unter',
      },
      loadFailed: 'Karten konnten nicht geladen werden.',
      submitFailed: 'Vorgang fehlgeschlagen. Bitte erneut versuchen.',
      none: 'Noch keine Karten.',
      holder: 'Karteninhaber',
      card: 'Karte',
      currency: 'Währung',
      balance: 'Guthaben',
      available: 'Verfügbar',
      creditLimit: 'Kreditlimit',
      used: 'Verbraucht',
      status: 'Status',
      actions: 'Aktionen',
      created: 'Erstellt',
      amount: 'Betrag',
      type_stored_value: 'Guthabenkarte',
      type_credit_limit: 'Kreditkarte',
      status_active: 'Aktiv',
      status_frozen: 'Gesperrt',
      status_closed: 'Geschlossen',
      freeze: 'Sperren',
      unfreeze: 'Entsperren',
      close: 'Karte schließen',
      confirmClose: 'Diese Karte schließen? Dies kann nicht rückgängig gemacht werden.',
      confirmCloseYes: 'Schließen bestätigen',
      cancel: 'Abbrechen',
      create: 'Erstellen',
      creating: 'Wird erstellt…',
      holderNameLabel: 'Name des Karteninhabers',
      currencyLabel: 'Währung',
      unlimited: 'Unbegrenzt',
      reveal: {
        action: 'Kartennummer anzeigen',
        title: 'Kartendetails',
        cardNumber: 'Kartennummer',
        cvv: 'CVV',
        expiry: 'Ablauf (JJ/MM)',
        holder: 'Karteninhaber',
        warning: 'Sensibel — nicht teilen oder speichern. Schließt beim Verwerfen.',
        close: 'Fertig',
        failed: 'Kartendetails konnten nicht geladen werden.',
      },
      amountHint: 'Betrag in Haupteinheiten eingeben (z. B. 100,00).',
      kyc: {
        section: 'Karteninhaber & Kartendetails',
        cardBin: 'Karten-BIN-ID',
        amount: 'Anfangsbetrag',
        allowOnline: 'Online-Transaktionen erlauben',
        firstName: 'Vorname',
        lastName: 'Nachname',
        mobilePrefix: 'Vorwahl',
        mobile: 'Mobil',
        email: 'E-Mail',
        birthDate: 'Geburtsdatum',
        region: 'Kartenregion (ISO-3)',
        country: 'Rechnungsland (ISO-3)',
        state: 'Bundesland/Provinz',
        city: 'Stadt',
        address: 'Adresse',
        postcode: 'Postleitzahl',
      },
      stored: {
        subtitle: 'Guthabenkarten — aufladen und innerhalb des Guthabens ausgeben.',
        addCard: 'Neue Guthabenkarte',
        closeForm: 'Formular schließen',
        newCardTitle: 'Neue Guthabenkarte',
        topUp: 'Aufladen',
        topUpTitle: 'Karte aufladen',
        amountLabel: 'Aufladebetrag',
        confirmTopUp: 'Aufladung bestätigen',
        emptyHint: 'Erstellen Sie eine Guthabenkarte, um ein Prepaid-Guthaben zu laden.',
      },
      credit: {
        subtitle: 'Kreditkarten — bis zum genehmigten Kreditrahmen ausgeben.',
        addCard: 'Neue Kreditkarte',
        closeForm: 'Formular schließen',
        newCardTitle: 'Neue Kreditkarte',
        creditLimitLabel: 'Kreditlimit',
        setLimit: 'Limit anpassen',
        setLimitTitle: 'Kreditlimit anpassen',
        newLimitLabel: 'Neues Kreditlimit',
        newLimitHint: 'Darf nicht niedriger als der bereits verbrauchte Betrag sein.',
        confirmSetLimit: 'Bestätigen',
        emptyHint: 'Erstellen Sie eine Kreditkarte, um einen Kreditrahmen zu gewähren.',
      },
      txns: {
        subtitle: 'Transaktionsverlauf für eine ausgewählte Karte.',
        selectCard: 'Karte',
        selectPlaceholder: 'Karte auswählen…',
        noCard: 'Keine Karten verfügbar. Erstellen Sie zuerst eine Karte.',
        empty: 'Keine Transaktionen für diese Karte.',
        colTime: 'Zeit',
        colType: 'Typ',
        colAmount: 'Betrag',
        colMemo: 'Notiz',
        colStatus: 'Status',
        type_spend: 'Ausgabe',
        type_topup: 'Aufladung',
        type_refund: 'Erstattung',
      },
      risk: {
        subtitle: 'Kartenspezifische Risikokontrollen — Transaktionslimits und Online-Nutzung.',
        selectCard: 'Karte',
        selectPlaceholder: 'Karte auswählen…',
        noCard: 'Keine Karten verfügbar. Erstellen Sie zuerst eine Karte.',
        perTxnLabel: 'Limit pro Transaktion',
        dailyLabel: 'Tageslimit',
        monthlyLabel: 'Monatslimit',
        limitHint: '0 bedeutet kein Limit.',
        allowOnline: 'Online-Transaktionen erlauben',
        save: 'Regeln speichern',
        saving: 'Wird gespeichert…',
        saved: 'Risikoregeln gespeichert.',
        loadFailed: 'Risikoregeln konnten nicht geladen werden.',
      },
    },
    enterprise: {
      membersTitle: 'Unternehmensmitglieder',
      invite: 'Mitglied einladen',
      inviteHint:
        'Sie können sich anmelden und dieses Unternehmen verwalten. Nur der Aktenverwalter kann einladen.',
      transfer: 'Unternehmen übertragen',
      transferConfirm: 'Übertragung senden',
      transferTo: 'Übertragen an (E-Mail)',
      transferHint:
        'Der Empfänger muss über einen einmaligen Link per E-Mail bestätigen. Bis dahin ändert sich nichts.',
      transferWarn:
        'Nach der Annahme wird er zum Aktenverwalter und Sie werden zum Administrator herabgestuft. Das können Sie nicht allein rückgängig machen.',
      transferSent:
        'Übertragungsanfrage gesendet. Sie wird erst nach Bestätigung per E-Mail wirksam.',
      remove: 'Entfernen',
      noMembers: 'Noch keine Mitglieder.',
      noEnterprise: 'Kein Unternehmen ausgewählt',
      colName: 'Name',
      colEmail: 'E-Mail',
      colRole: 'Rolle',
      colStatus: 'Status',
      colInvitedBy: 'Eingeladen von',
      colJoinedAt: 'Beigetreten',
      colActions: 'Aktionen',
      statusActive: 'Aktiv',
      statusPending: 'Eingeladen',
      roleOwner: 'Aktenverwalter',
      roleAdmin: 'Administrator',
      roleViewer: 'Betrachter',
      roleOwnerDesc:
        'allein verantwortlich für dieses Unternehmen; kann Mitglieder verwalten und das Unternehmen übertragen',
      roleAdminDesc:
        'kann das Geschäft betreiben (Profil, API-Schlüssel, Gelder), aber keine Mitglieder oder Eigentümerschaft ändern',
      roleViewerDesc: 'nur Lesezugriff',
      acceptTitle: 'Unternehmensübertragung annehmen',
      acceptConfirm: 'Annehmen und Aktenverwalter werden',
      acceptHint:
        'Mit der Annahme werden Sie Aktenverwalter dieses Unternehmens — mit Zugriff auf Gelder und KYB-Unterlagen und der damit verbundenen Compliance-Verantwortung.',
      acceptDone: 'Übertragung abgeschlossen',
      acceptDoneHint: 'Sie sind jetzt Aktenverwalter dieses Unternehmens.',
      acceptGoDashboard: 'Zum Dashboard',
      acceptNoToken:
        'Diesem Link fehlt das Bestätigungs-Token. Bitte nutzen Sie den Link aus der E-Mail.',
      switch: 'Unternehmen wechseln',
      myEnterprises: 'Meine Unternehmen',
      current: 'Unternehmen',
      none: 'Kein Unternehmen',
      empty: 'Noch keine Unternehmen.',
      create: 'Neues Unternehmen anlegen',
      resume: 'Fortsetzen',
    },
    appStatus: {
      draft: 'Entwurf',
      submitted: 'Eingereicht',
      kyb_in_review: 'In Prüfung',
      corporation_in_review: 'In Prüfung',
      compliance_review: 'Compliance-Prüfung',
      info_requested: 'Info angefordert',
      approved: 'Genehmigt',
      rejected: 'Abgelehnt',
      suspended: 'Ausgesetzt',
      invited: 'Eingeladen',
      unknown: '—',
    },
    verifStatus: {
      verified: 'Verifiziert',
      pending: 'Ausstehend',
      failed: 'Fehlgeschlagen',
      unverified: 'Nicht verifiziert',
    },
    bizType: {
      0: 'Nicht angegeben',
      1: 'Einzelunternehmen',
      2: 'Personengesellschaft',
      3: 'GmbH',
      4: 'Kapitalgesellschaft',
      5: 'Gemeinnützig',
      6: 'Behörde',
      7: 'Börsennotiert',
    },
    docState: {
      required: 'Erforderlich',
      uploaded: 'Hochgeladen',
      under_review: 'In Prüfung',
      verified: 'Verifiziert',
      rejected: 'Abgelehnt',
      expired: 'Abgelaufen',
    },
    profile: {
      title: 'Unternehmensprofil',
      subtitle: 'Schreibgeschützte Ansicht Ihres abgeschlossenen Onboarding-Profils.',
      loadError: 'Profil konnte nicht geladen werden.',
      noApp: 'Noch kein Unternehmensprofil.',
      noAppHint: 'Schließen Sie das Onboarding ab, um eines zu erstellen.',
      company: 'Unternehmensdaten',
      legalName: 'Rechtsname',
      businessType: 'Unternehmensart',
      incCountry: 'Gründungsland',
      incState: 'Bundesland / Region',
      incDate: 'Gründungsdatum',
      regNumber: 'Registernr.',
      industry: 'Branche',
      mcc: 'MCC',
      website: 'Website',
      phone: 'Telefon',
      supportEmail: 'Support-E-Mail',
      processingCountries: 'Abwicklungsländer',
      productDesc: 'Geschäftsbeschreibung',
      tax: 'Steuerkennungen',
      ein: 'EIN',
      vat: 'USt-IdNr.',
      uscc: 'Einheitl. Sozialkreditcode',
      brn: 'Handelsregisternr.',
      otherTax: 'Andere Steuer-ID',
      addresses: 'Adressen',
      registeredAddr: 'Registrierte Adresse',
      operatingAddr: 'Betriebsadresse',
      volume: 'Geschätztes Volumen',
      monthlyMin: 'Monatl. Min',
      monthlyMax: 'Monatl. Max',
      avgTxn: 'Ø Transaktion',
      maxTxn: 'Max. Transaktion',
      people: 'Wirtsch. Berechtigte & Schlüsselpersonen',
      noOwners: 'Keine wirtschaftlich Berechtigten (befreit).',
      pTitle: 'Titel',
      nationality: 'Nationalität',
      dob: 'Geb.-Datum',
      email: 'E-Mail',
      roleLegalRep: 'Gesetzl. Vertreter',
      roleDirector: 'Direktor',
      roleUbo: 'Wirtsch. Berechtigter',
      roleRep: 'Vertreter',
      documents: 'Dokumente',
      noDocuments: 'Keine Dokumente hochgeladen.',
      appId: 'Antrags-ID',
      verifDim: {
        legalEntity: 'Rechtsträger',
        taxId: 'Steuer-ID',
        address: 'Adresse',
        documents: 'Dokumente',
        aml: 'AML',
        sanctions: 'Sanktionen',
      },
    },
    activity: {
      title: 'Geschäftsaktivität',
      notProvisioned:
        'Dieses Unternehmen hat noch kein Konto — Aktivität erscheint nach Kontoeröffnung.',
      balance: 'Saldo',
      transactions: 'Letzte Transaktionen',
      noTxns: 'Noch keine Transaktionen.',
      time: 'Zeit',
      type: 'Typ',
      account: 'Konto',
      amount: 'Betrag',
    },
    settings: {
      subtitle: 'Unternehmensprofil verwalten und Geschäftsaktivität ansehen.',
      tabProfile: 'Unternehmensprofil',
      tabActivity: 'Geschäftsaktivität',
    },
    accountsPage: { subtitle: 'Konten des aktuellen Unternehmens.' },
    accountStatus: {
      active: 'Aktiv',
      pending: 'In Prüfung',
      inactive: 'Inaktiv',
      returned: 'Zurückgesendet',
      closed: 'Geschlossen',
      frozen: 'Eingefroren',
      suspended: 'Ausgesetzt',
      returned_info: 'Nachreichung angefordert',
      closed_pending: 'Wird geschlossen',
    },
    apiErrors: {
      SUBMERCHANT_NOT_EDITABLE:
        'Der Kanal erlaubt Änderungen nur, wenn der Antrag zur Nachreichung zurückgesendet wurde. Er befindet sich derzeit in Prüfung, Änderungen können noch nicht übernommen werden.',
      SUBMERCHANT_NOT_ACTIVE:
        'Der Kanal prüft Ihren Antrag noch. Das Empfangskonto wird nach Genehmigung automatisch eröffnet.',
      SUBMERCHANT_NOT_READY: 'Ihr Antrag wird vorbereitet. Bitte versuchen Sie es in Kürze erneut.',
      MATERIALS_REQUIRED:
        'Einige erforderliche Dokumente fehlen. Bitte vervollständigen Sie diese zuerst.',
      SETTINGS_UNAVAILABLE:
        'Ein Konfigurationsdienst ist vorübergehend nicht verfügbar. Bitte später erneut versuchen.',
      PYVIO_INDUSTRY_UNRESOLVED:
        'Die gewählte Branche ist für den Kanal noch nicht zugeordnet. Bitte kontaktieren Sie den Support.',
    },
    transactionsPage: { subtitle: 'Transaktionsverlauf des aktuellen Unternehmens.' },
    usersPage: {
      subtitle: 'Schlüsselpersonen des aktuellen Unternehmens (Direktoren, wirtsch. Berechtigte).',
    },
    pgPayees: {
      docSection: 'Geschäftskooperationsdokument',
      docHint: 'Laden Sie ein Dokument hoch, das die Geschäftsbeziehung zwischen diesem Zahlungsempfänger und Ihrem Unternehmen belegt (Vertrag, Vereinbarung usw.).',
      docUpload: 'Dokument hochladen',
      docUploading: 'Wird hochgeladen…',
      docRemove: 'Entfernen',
      uploadFailed: 'Upload fehlgeschlagen. Bitte erneut versuchen.',
      subtitle: 'Empfänger des aktuellen Unternehmens verwalten.',
      addPayee: 'Empfänger hinzufügen',
      closeForm: 'Schließen',
      empty: 'Noch keine Empfänger.',
      colName: 'Name',
      colKind: 'Typ',
      colCurrency: 'Währung',
      colMethod: 'Methode',
      colBank: 'Bank',
      colStatus: 'Status',
      delete: 'Löschen',
      confirmDelete: 'Diesen Empfänger löschen?',
      kind: 'Typ',
      kind_supplier: 'Lieferant',
      kind_own_account: 'Eigenes Konto',
      holderType: 'Inhabertyp',
      holder_personal: 'Privat',
      holder_company: 'Firma',
      paymentMethod: 'Zahlungsmethode',
      method_local: 'Lokal',
      method_swift: 'Swift',
      currency: 'Währung',
      remark: 'Bemerkung',
      profileSection: 'Empfängerdaten',
      bankSection: 'Bankdaten',
      legalName: 'Firmenname',
      companyNo: 'Handelsregisternr.',
      firstName: 'Vorname',
      lastName: 'Nachname',
      email: 'E-Mail',
      phone: 'Telefon',
      country: 'Land',
      city: 'Stadt',
      street: 'Straße',
      province: 'Bundesland / Region',
      postCode: 'PLZ',
      accountNo: 'Kontonr.',
      accountName: 'Kontoinhaber',
      bankName: 'Bankname',
      swiftCode: 'SWIFT / BIC',
      iban: 'IBAN',
      accountCountry: 'Kontoland',
      bankAddress: 'Bankadresse',
      ownAccountProofNote:
        'Ein eigenes Konto erfordert einen Banknachweis (z. B. Kontoauszug oder Kontobestätigung, aus der hervorgeht, dass das Konto Ihnen gehört).',
      bankProofUpload: 'Banknachweis hochladen',
      bankProofRequired: 'Bitte laden Sie zuerst den Banknachweis hoch.',
      uploadStillProcessing:
        'Das Dokument wird noch verarbeitet. Bitte versuchen Sie es in Kürze erneut.',
      submit: 'Empfänger hinzufügen',
      submitting: 'Speichern…',
      submitFailed: 'Empfänger konnte nicht gespeichert werden.',
    },
    pgTransfer: {
      subtitle: 'Überweisung oder Auszahlung vom aktuellen Unternehmen senden.',
      formTitle: 'Überweisung / Auszahlung',
      payee: 'Empfänger',
      selectPayee: 'Empfänger wählen',
      noPayees: 'Noch keine Empfänger. Bitte zuerst einen anlegen.',
      fromAccount: 'Von Konto',
      selectAccount: 'Konto wählen',
      noAccounts: 'Kein Verrechnungskonto verfügbar.',
      amount: 'Betrag',
      purpose: 'Verwendungszweck',
      postscript: 'Zusatz',
      fxNeeded: 'Währungsübergreifend ({{origin}} → {{target}}). Kurs sperren zum Fortfahren.',
      lockRate: 'Kurs sperren',
      relock: 'Kurs aktualisieren',
      quoting: 'Sperren…',
      rate: 'Kurs',
      estReceive: 'Voraussichtl. Gutschrift',
      expiresIn: 'Läuft ab in',
      expired: 'Abgelaufen',
      submit: 'Überweisung senden',
      submitting: 'Senden…',
      failed: 'Überweisung konnte nicht gesendet werden.',
      submitted: 'Überweisung gesendet',
      movementId: 'Bewegungs-ID',
      status: 'Status',
      newTransfer: 'Neue Überweisung',
    },
    dashboard: {
      title: 'Übersicht',
      overview: 'Übersicht',
      balances: 'Guthaben',
      recentActivity: 'Letzte Aktivität',
      noTxns: 'Noch keine Transaktionen.',
      noAccounts:
        'Sie haben noch keine Konten. Schließen Sie das Unternehmens-Onboarding ab, um eines zu eröffnen.',
      completeOnboarding: 'Onboarding abschließen',
      onbApprovedTitle: '✓ Unternehmens-Onboarding genehmigt',
      onbApprovedHint: 'Ihr Abwicklungskonto wird eingerichtet und erscheint in Kürze hier.',
      onbPendingTitle: 'Unternehmens-Onboarding in Prüfung',
      onbPendingHint:
        'Unternehmen und alle erforderlichen wirtschaftlich Berechtigten führen die ID&V-Prüfung durch.',
      onbFailedTitle:
        'Onboarding wurde nicht genehmigt. Bitte Compliance kontaktieren oder erneut einreichen.',
      onbViewStatus: 'Onboarding-Status ansehen',
      refresh: 'Aktualisieren',
      statAccounts: 'Konten',
      statBalance: 'Hauptsaldo',
      statActive: 'Aktiv',
      statOnboarding: 'Onboarding',
      statRecentTxns: 'Letzte Tx',
      quickActions: 'Schnellaktionen',
      qaApiKey: 'API-Schlüssel erstellen',
      qaSecurity: 'Sicherheitseinstellungen',
      qaOnboarding: 'Onboarding',
      txnId: 'ID',
      txnType: 'Typ',
      txnAmount: 'Betrag',
      txnDate: 'Datum',
      priceBuy: 'Kaufpreis',
      priceSell: 'Verkaufspreis',
      ovTitle: 'Gesamtsaldo',
      ovSub: 'Überblick über Ihr aktuelles Guthaben',
      primaryAccount: 'Hauptkonto',
      transfer: 'Überweisen',
      received: 'Erhalten',
      funding: 'Funding-Übersicht',
      income: 'Einnahmen',
      expense: 'Ausgaben',
      mIncome: 'Monatl. Einnahmen',
      mSpent: 'Gesamtausgaben',
      mSaving: 'Sparquote',
      comingSoon: 'Demnächst',
      orderId: 'Auftrags-ID',
      activity: 'Aktivität',
      price: 'Preis',
      status: 'Status',
      statusDone: 'Abgeschlossen',
      filter: 'Filter',
      search: 'Suchen',
      totalRevenue: 'Gesamtumsatz',
      range3: '3 Monate',
      range6: '6 Monate',
      range12: '1 Jahr',
      convertTitle: 'Umtausch',
      pair: 'Paar',
      side: 'Richtung',
      youPay: 'Sie zahlen',
      youReceive: 'Sie erhalten',
      sell: 'Verkaufen',
      buy: 'Kaufen',
      amount: 'Betrag',
      getQuote: 'Kurs abrufen',
      rate: 'Kurs',
      fee: 'Gebühr',
      youGet: 'Sie erhalten',
      convert: 'Jetzt umtauschen',
      convertOk: 'Umgetauscht',
    },
    login: {
      failed: 'E-Mail oder Passwort ist falsch.',
      mfaInvalid: 'Ungültiger Bestätigungscode.',
      locked: 'Konto vorübergehend gesperrt. Später erneut versuchen.',
      passkeyUnavailable: 'Passkey-Anmeldung nicht verfügbar. Bitte Passwort verwenden.',
      tagline: 'Zukunftsfähige Finanzkonsole',
      welcomeBack: 'Willkommen zurück',
      signInToContinue: 'Bitte anmelden, um fortzufahren',
      usernameOrEmail: 'Benutzername oder E-Mail',
      password: 'Passwort',
      forgotPassword: 'Passwort vergessen?',
      login: 'ANMELDEN',
      loggingIn: 'ANMELDUNG…',
      orUse: 'oder',
      passkeyLogin: 'Mit Passkey anmelden',
      verifying: 'Wird überprüft…',
      twoFactor: 'Zwei-Faktor-Authentifizierung',
      enterTotp: 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein',
      totpPlaceholder: '6-stelliger Code / Wiederherstellungscode',
      verifyAndLogin: 'Überprüfen & Anmelden',
      back: 'Zurück',
    },
    logs: {
      title: 'Aktivitätsprotokoll',
      subtitle: 'Sehen Sie die Anmelde- und Sicherheitsaktivitäten Ihres Kontos ein.',
      operation: 'Aktivität',
      source: 'Quelle',
      device: 'Gerät',
      ipAddr: 'IP-Adresse',
      location: 'Standort',
      requestId: 'Anfrage-ID',
      result: 'Status',
      time: 'Zeit',
      noRecords: 'Keine Aktivitätseinträge.',
      devApp: 'App',
      devMobile: 'Mobil',
      devTablet: 'Tablet',
      devDesktop: 'Desktop',
      devApi: 'API',
      devUnknown: 'Unbekannt',
      geoLan: 'Lokal',
      prev: 'Zurück',
      next: 'Weiter',
      success: 'Erfolgreich',
      failed: 'Fehlgeschlagen',
      pageInfo: '{{total}} gesamt · Seite {{page}}/{{pages}}',
      actLogin: 'Anmeldung',
      actRegister: 'Registrierung',
      actRefresh: 'Token-Aktualisierung',
      actTotpEnroll: 'Authenticator binden',
      actTotpDisable: 'Authenticator deaktivieren',
      actPasskeyRegister: 'Passkey hinzufügen',
      actPasskeyDelete: 'Passkey löschen',
      actStepup: 'Transaktions-Step-up',
      actApikeyCreate: 'API-Schlüssel erstellen',
      actApikeyRevoke: 'API-Schlüssel widerrufen',
    },
    onboarding: {
      title: 'Unternehmens-Registrierung',
      intro:
        'Reichen Sie Ihre Unternehmensdaten ein, um die KYB-Prüfung zu starten. Die Daten werden auf einer von Dritten (Sumsub) gehosteten Seite erfasst und geprüft; nach Genehmigung wird Ihr Konto eröffnet, danach können Sie auf der Seite „API-Schlüssel“ AK/SK für serverseitige Aufrufe ausstellen.',
      contactEmail: 'Kontakt-E-Mail',
      contactPhone: 'Kontakt-Telefon',
      country: 'Land / Region der Registrierung',
      bizType: 'Unternehmensart',
      submit: 'Registrierung starten',
      submitNote:
        'Nach dem Absenden wird ein extern gehosteter Prüflink erstellt, auf dem Unternehmensdetails, UBO (wirtschaftlich Berechtigte) und Dokumente eingetragen werden.',
      appId: 'Antragsnummer',
      verifiedMsg:
        '✓ Prüfung bestanden, Konto wird eröffnet. Danach unter „API-Schlüssel“ serverseitige Schlüssel ausstellen.',
      failedMsg:
        '✕ Prüfung nicht bestanden. Für Einspruch oder erneute Einreichung wenden Sie sich an den Compliance-Support.',
      goVerify: 'Zur Prüfseite',
      verifyNote:
        'Kehren Sie nach dem Ausfüllen auf der neuen Seite hierher zurück — der Status wird automatisch aktualisiert.',
      dimensions: 'Prüfdimensionen',
      restart: 'Registrierung neu starten',
      restartNote1:
        'Dies löscht den lokalen Verweis auf den aktuellen Antrag und kehrt zum Formular zurück.',
      restartNote2:
        'Bereits an das Backend übermittelte Anträge sind nicht betroffen; den Support für den Verlauf kontaktieren.',
      confirmRestart: 'Neustart bestätigen',
      starting: 'Wird gestartet…',
      submitFull: 'Registrierungsprüfung starten',
      provider: 'Prüfdienstleister',
      refresh: 'Aktualisieren',
      refreshing: 'Wird aktualisiert…',
      statusVerified: 'Bestanden',
      statusFailed: 'Nicht bestanden',
      statusPending: 'Wird geprüft',
      statusWaiting: 'Ausstehend',
      biz1: 'Einzelunternehmen',
      biz2: 'Personengesellschaft',
      biz3: 'Gesellschaft mit beschränkter Haftung (GmbH)',
      biz4: 'Aktiengesellschaft',
      biz5: 'Gemeinnützige Organisation',
      biz6: 'Behörde',
      biz7: 'Börsennotiertes Unternehmen',
      dimLegalEntity: 'Juristische Person',
      dimTaxId: 'Steuernummer',
      dimAddress: 'Eingetragene Adresse',
      dimDocuments: 'Dokumente',
      dimAml: 'Geldwäscheprävention',
      dimSanctions: 'Sanktionsprüfung',
      expiryNever: 'Läuft nie ab',
      expiry30: '30 Tage',
      expiry90: '90 Tage',
      expiry1y: '1 Jahr',
      // ── Tab ──
      tabEntity: 'Unternehmensdaten',
      tabBusiness: 'Geschäftsdaten',
      tabOwnership: 'Beteiligungsstruktur',
      tabPeople: 'Schlüsselpersonen',
      // ── Banner ──
      bannerStatus: 'Antragsnr.',
      bannerEcho: ' (Daten unten wiederhergestellt; jedes Element zeigt seinen Prüfstatus)',
      refreshShort: 'Aktualisieren',
      // ── Unternehmensprüfung ──
      companyVerifTitle: 'Unternehmensprüfung',
      // ── Unternehmensgrunddaten ──
      sectionCompanyBasics: 'Unternehmensgrunddaten',
      legalName: 'Juristischer Name',
      dba: 'Geschäftsbezeichnung (DBA)',
      regNumber: 'Registernummer',
      regNumberPh: 'Handelsregisternummer',
      taxIdPh: 'Steuer- / Registernummer (je nach Registrierungsland)',
      incState: 'Bundesland / Region der Gründung',
      incStatePh: 'Delaware / Hongkong',
      incDate: 'Gründungsdatum',
      // ── Eingetragene Adresse ──
      sectionRegAddress: 'Eingetragene Adresse',
      line1: 'Adresszeile 1',
      line2: 'Adresszeile 2',
      city: 'Stadt',
      state: 'Bundesland / Region',
      postal: 'Postleitzahl',
      addrCountry: 'Land / Region',
      // ── Geschäftsadresse ──
      sectionOpAddress: 'Geschäftsadresse',
      sameAsReg: 'Wie eingetragene Adresse',
      opSameHint: 'Die Geschäftsadresse entspricht der eingetragenen Adresse.',
      // ── Kontakt & Geschäft ──
      sectionContactBiz: 'Kontakt & Geschäft',
      companyPhone: 'Firmentelefon',
      website: 'Website',
      supportEmail: 'Support-E-Mail',
      mcc: 'MCC (Händlerkategorie-Code)',
      industry: 'Branche / Sektor',
      industryPh: 'Fintech / grenzüberschreitende Zahlungen',
      industryCat: 'Branchenkategorie',
      industrySub: 'Unterkategorie',
      industrySelect: 'Bitte wählen',
      ind: {
        financial_services: 'Finanzdienstleistungen',
        software_tech: 'Software & Technologie',
        ecommerce_retail: 'E-Commerce & Handel',
        professional_services: 'Professionelle Dienstleistungen',
        trade_logistics: 'Handel & Logistik',
        manufacturing: 'Fertigung & Industrie',
        media_content: 'Medien & Inhalte',
        health_life: 'Gesundheit & Biowissenschaften',
        travel_mobility: 'Reise & Mobilität',
        education: 'Bildung',
        realestate_construction: 'Immobilien & Bau',
        other: 'Sonstige',
        payments_acquiring: 'Zahlungen / Acquiring',
        remittance_fx: 'Überweisung / Devisen',
        ewallet_emi: 'E-Wallet / EMI',
        lending_credit: 'Kredit / Finanzierung',
        wealth_asset: 'Vermögensverwaltung',
        crypto_vasp: 'Krypto / VASP',
        insurtech: 'InsurTech',
        saas: 'SaaS / Unternehmenssoftware',
        cloud_infra: 'Cloud / Infrastruktur',
        ai_data: 'KI / Daten',
        cybersecurity: 'Cybersicherheit',
        blockchain_web3: 'Blockchain / Web3',
        devtools: 'Entwicklertools',
        b2b_ecommerce: 'B2B-E-Commerce',
        marketplace: 'Marktplatz',
        wholesale: 'Großhandel / Distribution',
        dtc_brand: 'DTC-Marke',
        retailtech: 'Retail-Tech',
        consulting: 'Beratung',
        legal: 'Rechtsdienstleistungen',
        accounting_audit: 'Buchhaltung / Prüfung',
        marketing_ad: 'Marketing / Werbung',
        hr_recruiting: 'HR / Recruiting',
        import_export: 'Import / Export',
        freight: 'Fracht / Logistik',
        supply_chain: 'Lieferkette',
        warehousing: 'Lagerung / Fulfillment',
        customs: 'Zoll / Verzollung',
        electronics: 'Elektronik / Hardware',
        industrial_equipment: 'Industrieausrüstung',
        consumer_goods: 'Konsumgüter',
        automotive: 'Automobil / Teile',
        chemicals: 'Chemie / Materialien',
        digital_media: 'Digitale Medien / Verlag',
        gaming: 'Gaming',
        adtech: 'AdTech',
        creator_economy: 'Creator Economy',
        digital_health: 'Digital Health',
        medical_devices: 'Medizinprodukte',
        biotech_pharma: 'Biotech / Pharma',
        health_services: 'Gesundheitsdienste',
        ota_travel: 'Reise / OTA',
        hospitality: 'Gastgewerbe / Beherbergung',
        ride_mobility: 'Mobilität / Fahrdienst',
        ticketing: 'Ticketing',
        edtech: 'EdTech',
        training_cert: 'Schulung / Zertifizierung',
        edu_services: 'Bildungsdienste',
        proptech: 'PropTech',
        construction: 'Bau / Ingenieurwesen',
        property_mgmt: 'Immobilienverwaltung',
        other_general: 'Sonstige',
      },
      productDesc: 'Produkt- / Dienstleistungsbeschreibung',
      productDescPh: 'Beschreiben Sie kurz Ihr Produkt, Ihre Zielkunden und den Geldfluss',
      // ── Geschätztes Volumen ──
      sectionVolume: 'Geschätztes Geschäftsvolumen',
      currency: 'Währung',
      volMin: 'Monatsvolumen (min.)',
      volMax: 'Monatsvolumen (max.)',
      volAvg: 'Durchschnittliche Transaktion',
      volHigh: 'Höchste Transaktion',
      // ── Börsennotiert ──
      listedLabel:
        'Dies ist ein börsennotiertes Unternehmen (Notierung kann von der UBO-Durchschau befreien)',
      exchange: 'Börse',
      ticker: 'Tickersymbol',
      // ── Beteiligungsstruktur ──
      ownTitle: 'Beteiligungsstruktur (mehrstufig)',
      ownIntro:
        'Ihr Unternehmen steht oben; klicken Sie auf „+ Gesellschafter“ an einem Knoten, um die Beteiligungskette nach unten zu erweitern (Organigramm von oben nach unten). Tragen Sie an jedem Ast den direkten Beteiligungs-% ein. Mehrstufige Durchschau wird unterstützt; das System erkennt UBOs entlang jeder Kette automatisch.',
      ownHint:
        'Tipp: Bei vielen Ebenen fahren Sie mit der Maus über einen Knoten, um seine gesamte Beteiligungskette bis zu „Ihrem Unternehmen“ hervorzuheben; jede Ebene ist farblich gekennzeichnet. Eine natürliche Person mit einer Durchschau-Beteiligung von 25% wird als UBO markiert.',
      thisCompany: 'Ihr Unternehmen',
      thisCompanyTag: 'Ihr Unternehmen',
      nodePerson: 'Natürliche Person',
      nodeEntity: 'Rechtsträger',
      nodeNamePh: 'Name des Gesellschafters',
      uboBadge: 'UBO · Durchschau {{pct}}%',
      uboSummaryTitle: 'Wirtschaftlich Berechtigte (UBO)',
      uboSummaryHint: 'alle Durchschau-Personen; ≥ {{threshold}} % sind UBOs (ID&V erforderlich)',
      effPct: 'Durchschau {{pct}} %',
      uboUnnamed: '(ohne Name)',
      zoomIn: 'Vergrößern',
      zoomOut: 'Verkleinern',
      zoomReset: 'Zoom zurücksetzen',
      zoomFit: 'An Bildschirm anpassen',
      addEntityShareholder: 'Rechtsträger als Gesellschafter hinzufügen',
      addPersonShareholder: 'Natürliche Person als Gesellschafter hinzufügen',
      removeShareholder: 'Diesen Gesellschafter und seine vorgelagerten Eigentümer entfernen',
      directSum: 'Direkte Beteiligung gesamt {{sum}}%',
      directSumOk: ' ✓',
      directSumBad: ' (sollte 100% sein, Einreichung nicht möglich)',
      noBoDeclare:
        'Erkläre keine wirtschaftlich Berechtigten (börsennotierte Unternehmen usw. können von der Durchschau befreit sein)',
      ownConfirm:
        'Ich bestätige, dass die obige Beteiligungs- / UBO-Struktur wahr und vollständig ist, und verstehe, dass jede erforderliche Person ihre eigene ID&V-Identitätsprüfung abschließen muss.',
      // ── Schlüsselpersonen ──
      peopleTitle: 'Schlüsselpersonen',
      peopleIntroA: 'Sie müssen mindestens eine(n) ',
      peopleRoleBo: 'wirtschaftlich Berechtigten',
      peopleIntroB: ', eine(n) ',
      peopleRoleLr: 'gesetzlichen Vertreter',
      peopleIntroC: ' und eine(n) ',
      peopleRoleFa: 'Aktenverwalter',
      peopleIntroD:
        ' benennen (eine Person kann mehrere Rollen innehaben oder die Rollen können auf mehrere Personen verteilt werden). Wirtschaftlich Berechtigte, die die Schwelle (üblicherweise 25%) erreichen, sowie alle Direktoren / gesetzlichen Vertreter / Aktenverwalter müssen die ID&V-Prüfung abschließen. ',
      peopleIntroEmail:
        'Die E-Mail des Aktenverwalters wird als Kontakt-E-Mail für diesen Antrag verwendet.',
      personTag: 'Schlüsselperson',
      fromTree: 'Aus Beteiligungsstruktur · Durchschau {{pct}}%',
      idvLabel: 'ID&V {{status}}',
      remove: 'Entfernen',
      firstName: 'Vorname',
      lastName: 'Nachname',
      dob: 'Geburtsdatum',
      nationality: 'Staatsangehörigkeit',
      personEmail: 'E-Mail (für ID&V-Einladung)',
      titleLabel: 'Funktion',
      titlePh: 'CEO / Direktor',
      ownershipPct: 'Beteiligungsanteil (%)',
      roleBo: 'Wirtschaftlich Berechtigter',
      roleDirector: 'Direktor',
      roleLr: 'Gesetzlicher Vertreter',
      roleFa: 'Aktenverwalter',
      addOwner: '+ Wirtschaftlich Berechtigten / Verwalter hinzufügen',
      // ── ID&V Status ──
      idvPassed: 'Bestanden',
      idvFailed: 'Nicht bestanden',
      idvPending: 'In Prüfung',
      idvWaiting: 'Ausstehend',
      // ── Aktionen ──
      submitHint:
        'Bestätigen Sie vor dem Absenden die Struktur im Tab „Beteiligungsstruktur“; die Informationen aus allen Tabs werden gemeinsam übermittelt.',
      saving: 'Wird gespeichert…',
      saveDraft: 'Entwurf speichern',
      submitting: 'Wird übermittelt…',
      submitApply: 'Antrag absenden & Prüfung starten',
      // ── Nach Einreichung ──
      progressLabel: 'Prüffortschritt: ',
      progressDoneSuffix: ' erforderliche Element(e) bestanden',
      checklistTitle: 'ID&V-Checkliste',
      taskCompany: 'Unternehmenseinheit',
      taskPerson: 'Beteiligte Person',
      taskRequired: 'Erforderlich',
      taskHolding: 'Beteiligung {{pct}}%',
      goVerifyShort: 'Prüfen',
      checklistFooter:
        'Jede erforderliche Person öffnet ihren eigenen Link, um die ID&V abzuschließen; sobald die Unternehmenseinheit und alle erforderlichen Personen bestanden haben, wird das Konto automatisch eröffnet.',
      // ── Validierung / Hinweise ──
      errLegalName: 'Bitte den juristischen Namen des Unternehmens eingeben',
      errRegNumber: 'Bitte die Registernummer des Unternehmens eingeben',
      errTaxId: 'Bitte {{label}} eingeben (Steuernummer erforderlich)',
      errIncState: 'Bitte das Bundesland / die Region der Gründung eingeben',
      errIncDate: 'Bitte das Gründungsdatum eingeben',
      errRegAddress:
        'Bitte die eingetragene Adresse vollständig ausfüllen (Zeile 1 / Stadt / Bundesland / PLZ)',
      errFileAdmin: 'Bitte einen Aktenverwalter benennen',
      errFileAdminEmail:
        'Der Aktenverwalter benötigt eine gültige E-Mail (wird als Kontakt-E-Mail verwendet)',
      errLegalRep: 'Bitte einen gesetzlichen Vertreter benennen',
      errBeneficialOwner:
        'Bitte einen wirtschaftlich Berechtigten benennen oder die Erklärung „keine wirtschaftlich Berechtigten“ ankreuzen',
      errOwnershipSum:
        '„{{name}}“ direkte Beteiligung beträgt insgesamt {{sum}}%, sollte aber 100% betragen',
      errOwnershipConfirm:
        'Bitte bestätigen Sie vor dem Absenden, dass die Beteiligungs- / UBO-Struktur wahr ist',
      draftEntityPrefix:
        'Bitte füllen Sie „Unternehmensdaten“ aus, bevor Sie den Entwurf speichern: ',
      noticeDraftSaved: 'Entwurf gespeichert',
      errDraftFailed: 'Entwurf konnte nicht gespeichert werden: ',
      errSubmitFailed: 'Einreichung fehlgeschlagen: ',
      errStatusFailed: 'Statusabfrage fehlgeschlagen: ',
      msgFallback: 'Anfrage fehlgeschlagen, bitte später erneut versuchen',
      // ── Steuernummer-Bezeichnung ──
      taxFallback: 'Steuer- / Registernummer',
      tax_US: 'EIN (US-Arbeitgebernummer)',
      tax_CN: 'Einheitlicher Sozialkreditcode',
      tax_DE: 'Steuernummer',
      tax_GB: 'Handelsregisternr.',
      tax_FR: 'Unternehmens-ID (SIREN)',
      tax_AU: 'Unternehmensnr. (ABN)',
      tax_SG: 'Unternehmensnr. (UEN)',
      tax_HK: 'Handelsregisternr. (BRN)',
      tax_JP: 'Unternehmensnr.',
      tax_CA: 'Unternehmensnr. (BN)',
      // ── Dokumente-Tab ──
      tabDocuments: 'Dokumente',
      docsCompanyTitle: 'Unternehmensdokumente',
      docsCompanyHint:
        'Laden Sie die Nachweisdokumente des Unternehmens hoch. Pflichtdokumente sind erforderlich; bedingte Dokumente werden nur bei Bedarf angefordert (z. B. erweiterte Sorgfaltsprüfung).',
      docsPersonTitle: 'Personendokumente',
      docsPersonHint:
        'Laden Sie Ausweis- und Adressnachweise für jede Schlüsselperson hoch (UBO / Direktor / gesetzlicher Vertreter / Aktenverwalter).',
      docsNoPersons:
        'Noch keine Personen gespeichert. Speichern Sie zuerst den Antrag (Entwurf speichern) und laden Sie dann hier Personendokumente hoch.',
      docRequired: 'Pflicht',
      docConditional: 'Bedingt',
      docUpload: 'Hochladen',
      docUploading: 'Wird hochgeladen…',
      docDelete: 'Löschen',
      docNeedApp:
        'Bitte speichern Sie zuerst den Antrag (Entwurf speichern) und laden Sie dann Dokumente hoch.',
      docUploadedOk: '{{name}} hochgeladen',
      docUploadFailed: 'Hochladen fehlgeschlagen: ',
      docDeleteFailed: 'Löschen fehlgeschlagen: ',
      docState_required: 'Erforderlich',
      docState_uploaded: 'Hochgeladen',
      docState_under_review: 'In Prüfung',
      docState_verified: 'Verifiziert',
      docState_rejected: 'Abgelehnt',
      docState_expired: 'Abgelaufen',
      docTypes: {
        certificate_of_incorporation: 'Gründungsurkunde',
        articles_of_association: 'Satzung / Gesellschaftsvertrag',
        proof_of_registered_address: 'Nachweis der Geschäftsadresse (max. 3 Monate)',
        ubo_declaration: 'UBO-Erklärung',
        business_license: 'Gewerbeerlaubnis',
        bank_statement: 'Kontoauszug (max. 3 Monate)',
        financial_statements: 'Jahresabschluss (EDD)',
        certificate_of_good_standing: 'Unbedenklichkeitsbescheinigung',
        board_resolution: 'Vorstandsbeschluss',
        tax_registration: 'Steuerregistrierungsbescheinigung',
        register_of_directors: 'Verzeichnis der Direktoren',
        register_of_shareholders: 'Gesellschafterverzeichnis',
        passport_or_national_id: 'Reisepass / Personalausweis',
        proof_of_address: 'Adressnachweis (max. 3 Monate)',
        power_of_attorney: 'Vollmacht',
        source_of_wealth_evidence: 'Nachweis der Vermögensherkunft (EDD)',
        nnc1_incorporation_form: 'Gründungsformular (NNC1)',
        nar1_annual_return: 'Jahresmeldung (NAR1)',
        significant_controllers_register: 'Register der maßgeblichen Kontrolleure (SCR)',
        acra_business_profile: 'ACRA-Unternehmensprofil',
        asic_company_extract: 'ASIC-Unternehmensauszug',
        confirmation_statement: 'Confirmation Statement (CS01)',
        psc_register: 'PSC-Register',
        gesellschafterliste: 'Gesellschafterliste',
        handelsregisterauszug: 'Handelsregisterauszug',
        b1_annual_return: 'Jahresmeldung (Form B1)',
        company_extract: 'Unternehmensauszug',
        particulars_of_shareholding: 'Angaben zur Beteiligung',
        extrait_kbis: 'Handelsregisterauszug (Kbis)',
        statuts_articles_of_association: 'Satzung (Statuts)',
        beneficial_owners_declaration: 'Register der wirtschaftlich Berechtigten (RBE)',
        kvk_business_register_extract: 'KvK-Handelsregisterauszug',
        shareholder_register: 'Aktionärsregister',
        ubo_register_extract: 'UBO-Registerauszug',
        commercial_register_extract: 'Handelsregisterauszug',
        share_register_aktienregister: 'Aktienregister',
        commercial_register_extract_gmbh_members: 'Handelsregisterauszug (GmbH-Gesellschafter)',
        registro_mercantil_extract: 'Handelsregisterauszug (Nota Simple)',
        good_standing_certificate: 'Unbedenklichkeitsbescheinigung',
        escritura_de_constitucion: 'Gründungsurkunde (Escritura)',
        beneficial_ownership_certificate: 'Bescheinigung des wirtschaftlich Berechtigten',
        visura_camerale: 'Handelskammerauszug (Visura)',
        certificate_of_all_historical_matters: 'Bescheinigung aller historischen Angaben',
        shareholder_registry_certificate: 'Bescheinigung des Aktionärsregisters',
        memorandum_of_association: 'Gründungsvertrag (MOA)',
        share_certificate: 'Aktienzertifikat / Mitgliederregister',
        certificate_of_incumbency: 'Amtsbescheinigung (Certificate of Incumbency)',
        board_resolution_appointment: 'Vorstandsbeschluss (Bestellung)',
        cap_table: 'Cap Table (Kapitalisierungstabelle)',
        stock_ledger: 'Aktienregister (Stock Ledger)',
        operating_agreement: 'LLC-Betriebsvereinbarung',
        statement_of_information: 'Informationserklärung (Statement of Information)',
        corporate_bylaws: 'Unternehmenssatzung (Bylaws)',
        corporate_profile_report: 'Unternehmensprofilbericht',
        isc_register: 'Register der Personen mit erheblicher Kontrolle (ISC)',
      },
    },
    security: {
      title: 'Kontosicherheit',
      subtitle:
        'Binden Sie Zwei-Faktor-Authentifizierung oder einen Passkey, um Konto und Transaktionen zu schützen.',
      enrollPrompt1: 'Bitte binden Sie zur Transaktionssicherheit mindestens eines von ',
      enrollPrompt2: ' bevor Sie fortfahren.',
      totp: 'Zwei-Faktor (TOTP)',
      totpDesc: 'Verwenden Sie eine Authenticator-App wie Google Authenticator / Authy.',
      bindTotp: 'Authenticator-Code binden',
      disableTotp: 'Zwei-Faktor deaktivieren',
      scanStep: '① Mit der Authenticator-App scannen oder den Schlüssel manuell eingeben:',
      confirmStep: '② Geben Sie den 6-stelligen Code aus der App zur Bestätigung ein:',
      confirmBind: 'Bindung bestätigen',
      recoveryIntro:
        '✅ Aktiviert. Bewahren Sie die folgenden einmaligen Wiederherstellungscodes sicher auf (bei Verlust des Authenticators; nur einmal angezeigt):',
      savedRecovery: 'Ich habe sie gespeichert',
      passkey: 'Passkey',
      passkeyDesc:
        'Passwortlose Anmeldung per Gerätebiometrie / Sicherheitsschlüssel; selbst ein starker zweiter Faktor.',
      addPasskey: 'Passkey hinzufügen',
      noPasskey: 'Noch kein Passkey hinzugefügt.',
      currentCode: 'Aktueller Code / Wiederherstellungscode',
      totpCode: '6-stelliger Code',
      deviceAlias: 'Geräte-Alias (z. B. Mein Handy)',
      enabled: 'Aktiviert',
      disabled: 'Deaktiviert',
      or: 'oder',
      totpCodeName: 'TOTP-Authenticator-Code',
      unnamedDevice: 'Unbenanntes Gerät',
      addedOn: 'Hinzugefügt am',
      delete: 'Löschen',
    },
    apikeys: {
      title: 'API-Schlüssel',
      subtitle:
        'Verwalten Sie Zugriffsschlüssel (AK/SK) für serverseitige Aufrufe. Schlüssel werden über customer → auth ausgestellt; das Gateway prüft Signaturen, um Anfragen zuzulassen.',
      create: 'API-Schlüssel erstellen',
      all: 'Alle',
      active: 'Aktiv',
      revoked: 'Widerrufen',
      owner: 'Inhaber',
      application: 'application',
      merchant: 'merchant',
      refresh: 'Aktualisieren',
      empty: 'Noch keine API-Schlüssel',
      createFirst: 'Ersten API-Schlüssel erstellen',
      name: 'Name',
      accessKey: 'Access Key',
      scopes: 'Scopes',
      createdAt: 'Erstellt',
      lastUsed: 'Zuletzt verwendet',
      expiry: 'Läuft ab',
      none: 'Keine',
      copied: 'Kopiert',
      revoke: 'Widerrufen',
      scopesLabel: 'Scopes',
      validity: 'Gültigkeit',
      secretOnce:
        '⚠️ Der Secret Key wird hier nur einmal angezeigt und ist nach dem Schließen nicht mehr einsehbar — bitte sofort speichern.',
      ak: 'Access Key (AK)',
      sk: 'Secret Key (SK)',
      savedIt: 'Ich habe ihn gespeichert',
      revokeTitle: 'API-Schlüssel widerrufen',
      revokeConfirm1: 'Widerruf bestätigen für „',
      revokeConfirm2:
        'Dies ist unumkehrbar; alle Aufrufe mit diesem Schlüssel schlagen sofort fehl.',
      confirmRevoke: 'Widerruf bestätigen',
      namePlaceholder: 'z. B. gateway-prod',
      copy: 'Kopieren',
      owns: 'Inhaber',
      creating: 'Wird erstellt…',
      createShort: 'Erstellen',
      neverUsed: 'Nie',
      neverExpire: 'Nie',
      createdSuccess: '„{{name}}“ erstellt',
    },
  },
  'zh-TW': {
    register: {
      title: '完成註冊',
      subtitle: '完成註冊以啟用您的帳戶',
      checking: '正在校驗邀請…',
      contactUs: '請聯絡我們重新發放邀請。',
      backToLogin: '返回登入',
      email: '電子郵件',
      emailLocked: '此電子郵件由邀請指定，不可修改。',
      name: '姓名',
      password: '密碼',
      passwordHint: '至少 8 位。',
      confirm: '確認密碼',
      submitting: '註冊中…',
      submit: '完成註冊',
      hasAccount: '已有帳戶？',
      reasonMissing: '缺少邀請碼',
      reasonExpired: '此邀請已過期',
      reasonUsed: '此邀請已被使用',
      reasonRevoked: '此邀請已被撤銷',
      reasonInvalid: '邀請無效',
    },
    common: {
      prev: '上一頁',
      next: '下一頁',
      search: '搜尋...',
      logout: '登出',
      viewAll: '查看全部',
      menu: '選單',
      home: '首頁',
      console: '控制台',
      superAdmin: '超級管理員',
      adminUser: '管理員',
      id: '編號',
      user: '使用者',
      amount: '金額',
      status: '狀態',
      date: '日期',
      actions: '操作',
      language: '語言',
      save: '儲存',
      cancel: '取消',
      confirm: '確認',
      loading: '載入中...',
      copiedHint: '已複製到剪貼簿',
      dblToCopy: '雙擊複製',
      copy: '複製',
      fullAddress: '完整地址',
      recvAddress: '收款地址',
      walletWarn: '僅向此地址轉入 {{coin}}（{{chain}} 鏈）。轉錯鏈或錯幣種，資金可能永久丟失。',
      searchCountry: '搜尋國家或區號',
      noMatch: '無相符結果',
      backToLogin: '返回登入',
      completingLogin: '正在完成登入…',
    },
    nav: {
      members: '成員',
      dashboard: '儀表板',
      onboarding: '企業入駐',
      apikeys: 'API 金鑰',
      security: '安全',
      logs: '操作日誌',
      accounts: '帳戶',
      transactions: '交易',
      payees: '收款人',
      transfer: '轉賬',
      users: '使用者',
      settings: '設定',
      profile: '企業檔案',
      // 菜单分组标题（不可点）
      group: {
        cards: '虛擬卡',
        global: '全球帳戶',
        stats: '數據統計',
        other: '其他',
      },
      // 虚拟卡组占位项
      cards: {
        stored: '儲值卡',
        credit: '額度卡',
        txns: '交易記錄',
        risk: '風控中心',
        holders: '持卡人',
      },
      reports: '報表',
      statements: '帳單',
    },
    // 占位页文案
    placeholder: {
      title: '功能建設中',
      desc: '該功能正在開發中，敬請期待。',
    },
    pgHolders: {
      colActions: '操作',
      edit: '編輯',
      editTitle: '編輯持卡人',
      save: '儲存',
      title: '持卡人',
      subtitle: '持卡人檔案登記——同一持卡人多張卡復用，無需重複填寫資料。',
      add: '新增持卡人',
      empty: '尚無持卡人。',
      colName: '姓名',
      colMember: '綁定成員',
      colEmail: '郵箱',
      colMobile: '手機',
      colCreated: '建立時間',
      addTitle: '新增持卡人',
      member: '綁定成員',
      memberNone: '無',
      firstName: '名',
      lastName: '姓',
      email: '郵箱',
      mobilePrefix: '區號',
      mobile: '手機',
      birthDate: '出生日期',
      region: '卡區域 (alpha-3)',
      countryCode: '國家 (alpha-3)',
      state: '省/州',
      city: '城市',
      address: '地址',
      postcode: '郵編',
      submit: '新增',
      nameRequired: '姓和名必填。',
    },
    pgCards: {
      pageOf: '第 {{page}} / {{total}} 頁',
      holderReuse: {
        new: '新建持卡人',
        existing: '複用已有持卡人',
        hint: '複用已有持卡人，在其可用 BIN 上開一張新卡，無需重填 KYC。',
        pickHolder: '選擇持卡人',
        bin: '發卡 BIN',
        pickBin: '選擇 BIN',
        pickCurrency: '選擇幣別',
        network: '卡組織',
        pickNetwork: '選擇卡組織',
        sharedHint: '為已有持卡人開卡。該持卡人已有主卡則新卡為共享卡（額度從主卡餘額共享）；否則這張即為其主卡。',
        belowMin: '金額低於最低充值額 {{min}}。',
        aboveMax: '金額超過最高充值額 {{max}}。',
        limit: '額度（共享額度／首充）',
      },
      sub: {
        primary: '主卡',
        sub: '子卡',
        hint: '子卡繼承主卡的持卡人、幣種和卡 BIN；從主卡餘額裡消費，上限為子卡額度。',
        primaryCard: '歸屬主卡',
        pickPrimary: '選擇主卡',
        limit: '子卡額度',
        badge: '子卡',
      },
      loadFailed: '載入卡片失敗。',
      submitFailed: '操作失敗，請重試。',
      none: '尚無卡片。',
      holder: '持卡人',
      card: '卡片',
      currency: '幣別',
      balance: '餘額',
      available: '可用',
      creditLimit: '授信額度',
      used: '已用',
      status: '狀態',
      actions: '操作',
      created: '建立時間',
      amount: '金額',
      type_stored_value: '儲值卡',
      type_credit_limit: '額度卡',
      status_active: '正常',
      status_frozen: '已凍結',
      status_closed: '已註銷',
      freeze: '凍結',
      unfreeze: '解凍',
      close: '註銷卡片',
      confirmClose: '確定註銷此卡？此操作無法復原。',
      confirmCloseYes: '確認註銷',
      cancel: '取消',
      create: '建立',
      creating: '建立中…',
      holderNameLabel: '持卡人姓名',
      currencyLabel: '幣別',
      unlimited: '不限',
      reveal: {
        action: '查看卡號',
        title: '卡片明細',
        cardNumber: '卡號',
        cvv: 'CVV',
        expiry: '有效期（年/月）',
        holder: '持卡人',
        warning: '敏感資訊——請勿分享或儲存，關閉後即清除。',
        close: '完成',
        failed: '載入卡片明細失敗。',
      },
      amountHint: '請以主要單位輸入金額（例如 100.00）。',
      kyc: {
        section: '持卡人與卡片資料',
        cardBin: '卡 BIN ID',
        amount: '首充金額',
        allowOnline: '允許線上交易',
        firstName: '名',
        lastName: '姓',
        mobilePrefix: '手機區號',
        mobile: '手機',
        email: '電子郵件',
        birthDate: '出生日期',
        region: '卡片國別（ISO-3）',
        country: '帳單國別（ISO-3）',
        state: '州/省',
        city: '城市',
        address: '地址',
        postcode: '郵遞區號',
      },
      stored: {
        subtitle: '預付餘額卡——儲值後於餘額內消費。',
        addCard: '新增儲值卡',
        closeForm: '收起表單',
        newCardTitle: '新增儲值卡',
        topUp: '儲值',
        topUpTitle: '卡片儲值',
        amountLabel: '儲值金額',
        confirmTopUp: '確認儲值',
        emptyHint: '建立儲值卡以載入預付餘額。',
      },
      credit: {
        subtitle: '額度卡——於核准授信額度內消費。',
        addCard: '新增額度卡',
        closeForm: '收起表單',
        newCardTitle: '新增額度卡',
        creditLimitLabel: '授信額度',
        setLimit: '調整額度',
        setLimitTitle: '調整授信額度',
        newLimitLabel: '新授信額度',
        newLimitHint: '不得低於已使用金額。',
        confirmSetLimit: '確認',
        emptyHint: '建立額度卡以授予授信額度。',
      },
      txns: {
        subtitle: '所選卡片的交易紀錄。',
        selectCard: '卡片',
        selectPlaceholder: '請選擇卡片…',
        noCard: '尚無可用卡片，請先建立卡片。',
        empty: '此卡片尚無交易。',
        colTime: '時間',
        colType: '類型',
        colAmount: '金額',
        colMemo: '備註',
        colStatus: '狀態',
        type_spend: '消費',
        type_topup: '儲值',
        type_refund: '退款',
      },
      risk: {
        subtitle: '單卡風控——交易限額與線上使用。',
        selectCard: '卡片',
        selectPlaceholder: '請選擇卡片…',
        noCard: '尚無可用卡片，請先建立卡片。',
        perTxnLabel: '單筆限額',
        dailyLabel: '每日限額',
        monthlyLabel: '每月限額',
        limitHint: '0 代表不限。',
        allowOnline: '允許線上交易',
        save: '儲存規則',
        saving: '儲存中…',
        saved: '風控規則已儲存。',
        loadFailed: '載入風控規則失敗。',
      },
    },
    enterprise: {
      membersTitle: '企業成員',
      invite: '邀請成員',
      inviteHint: '對方將能登入並管理這家企業。只有資料管理員可以邀請。',
      transfer: '轉移企業',
      transferConfirm: '送出轉移請求',
      transferTo: '轉移給（電子郵件）',
      transferHint: '接收方須點擊我們寄出的一次性連結確認。在對方接受前，一切不變。',
      transferWarn: '對方接受後即成為資料管理員，您將降為管理員。此操作您無法單方面撤回。',
      transferSent: '轉移請求已寄出。須待接收方以郵件確認後才會生效。',
      remove: '移除',
      noMembers: '尚無成員。',
      noEnterprise: '未選擇企業',
      colName: '姓名',
      colEmail: '電子郵件',
      colRole: '角色',
      colStatus: '狀態',
      colInvitedBy: '邀請人',
      colJoinedAt: '加入時間',
      colActions: '操作',
      statusActive: '已生效',
      statusPending: '已邀請',
      roleOwner: '資料管理員',
      roleAdmin: '管理員',
      roleViewer: '檢視者',
      roleOwnerDesc: '企業唯一的最終責任人；可增刪成員、轉移企業',
      roleAdminDesc: '可操作企業業務（開戶資料、API Key、資金），但不能異動成員與所有權',
      roleViewerDesc: '唯讀',
      acceptTitle: '接受企業轉移',
      acceptConfirm: '接受並成為資料管理員',
      acceptHint:
        '接受後，您將成為這家企業的資料管理員，可存取其資金與 KYB 檔案，並承擔相應的合規責任。',
      acceptDone: '轉移完成',
      acceptDoneHint: '您現在是這家企業的資料管理員。',
      acceptGoDashboard: '前往儀表板',
      acceptNoToken: '此連結缺少確認 token，請使用郵件中的連結。',
      switch: '切換企業',
      myEnterprises: '我的企業',
      current: '當前企業',
      none: '未選擇企業',
      empty: '尚無企業。',
      create: '新建企業',
      resume: '繼續填寫',
    },
    appStatus: {
      draft: '草稿',
      submitted: '已提交',
      kyb_in_review: '審核中',
      corporation_in_review: '審核中',
      compliance_review: '合規審核',
      info_requested: '待補件',
      approved: '已通過',
      rejected: '已拒絕',
      suspended: '已暫停',
      invited: '已邀請',
      unknown: '—',
    },
    verifStatus: { verified: '已核驗', pending: '審核中', failed: '未通過', unverified: '未核驗' },
    bizType: {
      0: '未指定',
      1: '獨資',
      2: '合夥',
      3: '有限責任公司',
      4: '股份公司',
      5: '非營利',
      6: '政府機構',
      7: '上市公司',
    },
    docState: {
      required: '待上傳',
      uploaded: '已上傳',
      under_review: '審核中',
      verified: '已核驗',
      rejected: '已退回',
      expired: '已過期',
    },
    profile: {
      title: '企業檔案',
      subtitle: '你已完成開戶的檔案只讀回顯。',
      loadError: '檔案載入失敗。',
      noApp: '尚無企業檔案。',
      noAppHint: '完成企業入駐後即可建立。',
      company: '公司資訊',
      legalName: '公司法定名稱',
      businessType: '企業類型',
      incCountry: '註冊國',
      incState: '州 / 省',
      incDate: '成立日期',
      regNumber: '註冊號',
      industry: '行業',
      mcc: 'MCC',
      website: '網站',
      phone: '電話',
      supportEmail: '客服信箱',
      processingCountries: '交易國家',
      productDesc: '業務描述',
      tax: '稅務標識',
      ein: 'EIN',
      vat: 'VAT 號',
      uscc: '統一社會信用代碼',
      brn: '商業登記號',
      otherTax: '其他稅號',
      addresses: '地址',
      registeredAddr: '註冊地址',
      operatingAddr: '營運地址',
      volume: '預估業務量',
      monthlyMin: '月下限',
      monthlyMax: '月上限',
      avgTxn: '平均單筆',
      maxTxn: '最高單筆',
      people: '受益人與關鍵人員',
      noOwners: '未申報受益人（豁免）。',
      pTitle: '職稱',
      nationality: '國籍',
      dob: '出生日期',
      email: '信箱',
      roleLegalRep: '法定代表人',
      roleDirector: '董事',
      roleUbo: '受益人',
      roleRep: '代表人',
      documents: '證件文件',
      noDocuments: '尚未上傳文件。',
      appId: '申請編號',
      verifDim: {
        legalEntity: '法定實體',
        taxId: '稅號',
        address: '地址',
        documents: '文件',
        aml: 'AML',
        sanctions: '制裁',
      },
    },
    activity: {
      title: '業務活動',
      notProvisioned: '該企業尚未開戶——開戶後這裡顯示業務活動。',
      balance: '餘額',
      transactions: '近期交易',
      noTxns: '尚無交易。',
      time: '時間',
      type: '類型',
      account: '帳戶',
      amount: '金額',
    },
    settings: {
      subtitle: '管理企業檔案並查看業務活動。',
      tabProfile: '企業檔案',
      tabActivity: '業務活動',
    },
    accountsPage: { subtitle: '當前企業的帳戶。' },
    accountStatus: {
      active: '已啟用',
      pending: '審核中',
      inactive: '未啟用',
      returned: '已退回',
      closed: '已關閉',
      frozen: '已凍結',
      suspended: '已暫停',
      returned_info: '待補件',
      closed_pending: '關閉中',
    },
    apiErrors: {
      SUBMERCHANT_NOT_EDITABLE:
        '通道方僅在申請被退回補件時才允許修改。目前仍在審核中，改動暫時無法同步。',
      SUBMERCHANT_NOT_ACTIVE: '通道方仍在審核您的申請，審核通過後會自動開立收款帳戶。',
      SUBMERCHANT_NOT_READY: '您的申請正在準備中，請稍後再試。',
      MATERIALS_REQUIRED: '尚有必填文件未提交，請先補齊。',
      SETTINGS_UNAVAILABLE: '設定服務暫時不可用，請稍後再試。',
      PYVIO_INDUSTRY_UNRESOLVED: '所選行業尚未配置通道方對應碼，請聯繫客服。',
    },
    transactionsPage: { subtitle: '當前企業的交易記錄。' },
    usersPage: { subtitle: '當前企業的關鍵人員（董事、受益人）。' },
    pgPayees: {
      docSection: '業務合作文檔',
      docHint: '上傳能證明此收款人與貴公司業務合作關係的文檔（合約、協議等）。',
      docUpload: '上傳文檔',
      docUploading: '上傳中…',
      docRemove: '移除',
      uploadFailed: '上傳失敗，請重試。',
      subtitle: '管理當前企業的收款人。',
      addPayee: '新增收款人',
      closeForm: '關閉',
      empty: '尚無收款人。',
      colName: '名稱',
      colKind: '類型',
      colCurrency: '幣種',
      colMethod: '方式',
      colBank: '銀行',
      colStatus: '狀態',
      delete: '刪除',
      confirmDelete: '確定刪除此收款人？',
      kind: '類型',
      kind_supplier: '供應商',
      kind_own_account: '本人帳戶',
      holderType: '持有人類型',
      holder_personal: '個人',
      holder_company: '公司',
      paymentMethod: '付款方式',
      method_local: '本地',
      method_swift: 'Swift',
      currency: '幣種',
      remark: '備註',
      profileSection: '收款人資料',
      bankSection: '銀行資料',
      legalName: '法定名稱',
      companyNo: '公司編號',
      firstName: '名',
      lastName: '姓',
      email: '電子郵件',
      phone: '電話',
      country: '國家',
      city: '城市',
      street: '街道',
      province: '省 / 州',
      postCode: '郵遞區號',
      accountNo: '帳號',
      accountName: '帳戶名稱',
      bankName: '銀行名稱',
      swiftCode: 'SWIFT / BIC',
      iban: 'IBAN',
      accountCountry: '帳戶國家',
      bankAddress: '銀行地址',
      ownAccountProofNote:
        '本人帳戶需上傳銀行證明（例如銀行對帳單或帳戶證明，用以佐證該帳戶屬於您本人）。',
      bankProofUpload: '上傳銀行證明',
      bankProofRequired: '請先上傳銀行證明文件。',
      uploadStillProcessing: '文件仍在處理中，請稍後再試。',
      submit: '新增收款人',
      submitting: '儲存中…',
      submitFailed: '收款人儲存失敗。',
    },
    pgTransfer: {
      subtitle: '從當前企業發起轉賬或付款。',
      formTitle: '轉賬 / 付款',
      payee: '收款人',
      selectPayee: '選擇收款人',
      noPayees: '尚無收款人，請先新增。',
      fromAccount: '轉出帳戶',
      selectAccount: '選擇帳戶',
      noAccounts: '無可用結算帳戶。',
      amount: '金額',
      purpose: '用途',
      postscript: '附言',
      fxNeeded: '跨幣種（{{origin}} → {{target}}）。請先鎖定匯率。',
      lockRate: '鎖定匯率',
      relock: '重新鎖定',
      quoting: '鎖定中…',
      rate: '匯率',
      estReceive: '預計到賬',
      expiresIn: '有效期',
      expired: '已過期',
      submit: '提交轉賬',
      submitting: '提交中…',
      failed: '轉賬提交失敗。',
      submitted: '轉賬已提交',
      movementId: '流水號',
      status: '狀態',
      newTransfer: '再轉一筆',
    },
    dashboard: {
      title: '儀表板',
      overview: '總覽',
      balances: '餘額',
      recentActivity: '近期動態',
      noTxns: '尚無交易紀錄。',
      noAccounts: '你還沒有帳戶。完成企業入駐後即可開立帳戶。',
      completeOnboarding: '前往企業入駐',
      onbApprovedTitle: '✓ 企業入駐已通過',
      onbApprovedHint: '結算帳戶開通中，稍後將顯示於此。',
      onbPendingTitle: '企業入駐審核中',
      onbPendingHint: '企業主體及全部必核驗受益人正在完成 ID&V 身分核驗。',
      onbFailedTitle: '入駐未通過，請聯絡合規或重新提交。',
      onbViewStatus: '查看入駐進度',
      refresh: '重新整理',
      statAccounts: '帳戶',
      statBalance: '主帳戶餘額',
      statActive: '已開通',
      statOnboarding: '入駐狀態',
      statRecentTxns: '近期交易',
      quickActions: '快捷操作',
      qaApiKey: '下發 API 金鑰',
      qaSecurity: '安全設定',
      qaOnboarding: '企業入駐',
      txnId: '交易號',
      txnType: '類型',
      txnAmount: '金額',
      txnDate: '日期',
      priceBuy: '買價',
      priceSell: '賣價',
      ovTitle: '總餘額',
      ovSub: '當前資金概覽',
      primaryAccount: '主帳戶',
      transfer: '轉賬',
      received: '收款',
      funding: '資金概覽',
      income: '收入',
      expense: '支出',
      mIncome: '月收入',
      mSpent: '月支出',
      mSaving: '儲蓄率',
      comingSoon: '待上線',
      orderId: '訂單號',
      activity: '活動',
      price: '金額',
      status: '狀態',
      statusDone: '已完成',
      filter: '篩選',
      search: '搜尋',
      totalRevenue: '總收入',
      range3: '3個月',
      range6: '半年',
      range12: '一年',
      convertTitle: '換匯',
      pair: '交易對',
      side: '方向',
      youPay: '支付',
      youReceive: '收到',
      sell: '賣出',
      buy: '買入',
      amount: '金額',
      getQuote: '取得報價',
      rate: '匯率',
      fee: '手續費',
      youGet: '實際到帳',
      convert: '立即換匯',
      convertOk: '換匯成功',
    },
    login: {
      failed: '電子郵件或密碼不正確。',
      mfaInvalid: '驗證碼不正確。',
      locked: '帳戶暫時鎖定，請稍後再試。',
      passkeyUnavailable: 'Passkey 登入目前無法使用，請改用密碼。',
      tagline: '面向未來的金融控制台',
      welcomeBack: '歡迎回來',
      signInToContinue: '請登入以繼續',
      usernameOrEmail: '使用者名稱或電子郵件',
      password: '密碼',
      forgotPassword: '忘記密碼？',
      login: '登入',
      loggingIn: '登入中…',
      orUse: '或使用',
      passkeyLogin: '使用 Passkey 登入',
      verifying: '驗證中…',
      twoFactor: '兩步驗證',
      enterTotp: '請輸入認證器 App 上的 6 位動態碼',
      totpPlaceholder: '6 位驗證碼 / 恢復碼',
      verifyAndLogin: '驗證並登入',
      back: '返回',
    },
    logs: {
      title: '操作日誌',
      subtitle: '查看你帳戶的登入與安全操作紀錄。',
      operation: '操作',
      source: '來源',
      device: '裝置',
      ipAddr: 'IP 位址',
      location: '位置',
      requestId: '請求 ID',
      result: '結果',
      time: '時間',
      noRecords: '尚無操作紀錄。',
      devApp: 'App',
      devMobile: '手機',
      devTablet: '平板',
      devDesktop: '桌面',
      devApi: 'API',
      devUnknown: '未知',
      geoLan: '本地',
      prev: '上一頁',
      next: '下一頁',
      success: '成功',
      failed: '失敗',
      pageInfo: '共 {{total}} 條 · 第 {{page}}/{{pages}} 頁',
      actLogin: '登入',
      actRegister: '註冊',
      actRefresh: '權杖重新整理',
      actTotpEnroll: '綁定動態驗證碼',
      actTotpDisable: '關閉動態驗證碼',
      actPasskeyRegister: '新增 Passkey',
      actPasskeyDelete: '刪除 Passkey',
      actStepup: '交易二次驗證',
      actApikeyCreate: '建立 API 金鑰',
      actApikeyRevoke: '撤銷 API 金鑰',
    },
    onboarding: {
      title: '企業入駐',
      intro:
        '提交企業資料以發起 KYB 驗證，資料由第三方（Sumsub）託管頁面採集與核驗；通過後自動開戶，隨後即可在「API 金鑰」頁簽發用於伺服器端呼叫的 AK/SK。',
      contactEmail: '聯絡信箱',
      contactPhone: '聯絡電話',
      country: '註冊國家 / 地區',
      bizType: '企業類型',
      submit: '發起入駐',
      submitNote:
        '提交後將產生第三方託管驗證連結，公司詳情、UBO（受益所有人）、證件等資料均在該頁面填報。',
      appId: '申請編號',
      verifiedMsg: '✓ 驗證已通過，帳戶開通中。開戶完成後可前往「API 金鑰」簽發伺服器端呼叫金鑰。',
      failedMsg: '✕ 驗證未通過。如需申訴或重新提交，請聯絡合規支援。',
      goVerify: '前往驗證頁填報資料',
      verifyNote: '在新頁面填完資料後返回本頁，狀態將自動重新整理。',
      dimensions: '核驗維度',
      restart: '重新發起入駐',
      restartNote1: '這會清除目前申請的本地引用並回到發起表單。',
      restartNote2: '已提交到後端的申請紀錄不受影響，可聯絡支援查詢歷史申請。',
      confirmRestart: '確認重新發起',
      starting: '發起中…',
      submitFull: '發起入駐驗證',
      provider: '驗證服務商',
      refresh: '重新整理',
      refreshing: '重新整理中…',
      statusVerified: '已通過',
      statusFailed: '未通過',
      statusPending: '驗證中',
      statusWaiting: '待驗證',
      biz1: '個人獨資',
      biz2: '合夥企業',
      biz3: '有限責任公司 (LLC)',
      biz4: '股份有限公司',
      biz5: '非營利組織',
      biz6: '政府機構',
      biz7: '上市公司',
      dimLegalEntity: '法人實體',
      dimTaxId: '稅號',
      dimAddress: '註冊地址',
      dimDocuments: '證明文件',
      dimAml: '反洗錢 (AML)',
      dimSanctions: '制裁篩查',
      expiryNever: '永不過期',
      expiry30: '30 天',
      expiry90: '90 天',
      expiry1y: '1 年',
      // ── Tab 標籤 ──
      tabEntity: '實體信息',
      tabBusiness: '業務信息',
      tabOwnership: '股權結構',
      tabPeople: '關鍵人員',
      // ── 回顯橫幅 ──
      bannerStatus: '申請編號',
      bannerEcho: '（資料已回顯，下方各項標註核驗狀態）',
      refreshShort: '重新整理',
      // ── 企業主體核驗徽章 ──
      companyVerifTitle: '企業主體核驗',
      // ── 公司基本信息 ──
      sectionCompanyBasics: '公司基本信息',
      legalName: '法定名稱',
      dba: '經營名稱 / 商號（DBA）',
      regNumber: '註冊號',
      regNumberPh: '公司註冊編號',
      taxIdPh: '稅務 / 登記號（按註冊國填寫）',
      incState: '註冊州 / 省',
      incStatePh: 'Delaware / 香港',
      incDate: '註冊日期',
      // ── 註冊地址 ──
      sectionRegAddress: '註冊地址',
      line1: '地址行 1',
      line2: '地址行 2',
      city: '城市',
      state: '州 / 省',
      postal: '郵編',
      addrCountry: '國家 / 地區',
      // ── 經營地址 ──
      sectionOpAddress: '經營地址',
      sameAsReg: '同註冊地址',
      opSameHint: '經營地址與註冊地址一致。',
      // ── 聯繫方式與業務 ──
      sectionContactBiz: '聯繫方式與業務',
      companyPhone: '公司電話',
      website: '官網',
      supportEmail: '客服信箱',
      mcc: 'MCC（商戶類別碼）',
      industry: '行業 / 業態',
      industryPh: '金融科技 / 跨境支付',
      industryCat: '行業大類',
      industrySub: '行業子類',
      industrySelect: '請選擇',
      ind: {
        financial_services: '金融服務',
        software_tech: '軟體與技術',
        ecommerce_retail: '電子商務與零售',
        professional_services: '專業服務',
        trade_logistics: '貿易與物流',
        manufacturing: '製造與工業',
        media_content: '媒體與內容',
        health_life: '健康與生命科學',
        travel_mobility: '旅遊與出行',
        education: '教育',
        realestate_construction: '房地產與建築',
        other: '其他',
        payments_acquiring: '支付 / 收單',
        remittance_fx: '匯款 / 換匯',
        ewallet_emi: '電子錢包 / EMI',
        lending_credit: '借貸 / 信貸',
        wealth_asset: '財富 / 資產管理',
        crypto_vasp: '加密資產 / VASP',
        insurtech: '保險科技',
        saas: 'SaaS / 企業軟體',
        cloud_infra: '雲端 / 基礎設施',
        ai_data: 'AI / 資料',
        cybersecurity: '網路安全',
        blockchain_web3: '區塊鏈 / Web3',
        devtools: '開發者工具',
        b2b_ecommerce: 'B2B 電商',
        marketplace: '市集平台',
        wholesale: '批發 / 分銷',
        dtc_brand: 'DTC 品牌',
        retailtech: '零售科技',
        consulting: '顧問諮詢',
        legal: '法律服務',
        accounting_audit: '會計 / 審計',
        marketing_ad: '行銷 / 廣告',
        hr_recruiting: '人力資源 / 招聘',
        import_export: '進出口貿易',
        freight: '貨運 / 物流',
        supply_chain: '供應鏈',
        warehousing: '倉儲 / 履約',
        customs: '報關 / 清關',
        electronics: '電子 / 硬體製造',
        industrial_equipment: '工業設備',
        consumer_goods: '消費品',
        automotive: '汽車 / 零部件',
        chemicals: '化工 / 材料',
        digital_media: '數位媒體 / 出版',
        gaming: '遊戲',
        adtech: '廣告科技',
        creator_economy: '創作者經濟',
        digital_health: '數位健康',
        medical_devices: '醫療器械',
        biotech_pharma: '生物科技 / 製藥',
        health_services: '健康服務',
        ota_travel: '旅遊平台 / OTA',
        hospitality: '酒店 / 住宿',
        ride_mobility: '出行 / 網約',
        ticketing: '票務',
        edtech: '線上教育',
        training_cert: '培訓 / 認證',
        edu_services: '教育服務',
        proptech: '房地產科技',
        construction: '建築 / 工程',
        property_mgmt: '物業管理',
        other_general: '其他',
      },
      productDesc: '產品 / 服務描述',
      productDescPh: '簡述你們的產品、目標客群與資金流',
      // ── 預估流水 ──
      sectionVolume: '預估業務流水',
      currency: '幣種',
      volMin: '月流水（最低）',
      volMax: '月流水（最高）',
      volAvg: '平均單筆金額',
      volHigh: '最高單筆金額',
      // ── 上市公司 ──
      listedLabel: '本公司為上市公司（上市可豁免受益人穿透）',
      exchange: '交易所',
      ticker: '股票代碼',
      // ── 股權結構 ──
      ownTitle: '股權結構（多層）',
      ownIntro:
        '本公司在頂部，點節點上的「+ 股東」逐層向下展開持股關係（自上而下組織架構圖式）；每條分支填直接持股 %。支持多層穿透，UBO 由系統按鏈路自動識別。',
      ownHint:
        '提示：層級較多時，把滑鼠移到任一節點上可高亮其到「本公司」的整條持股鏈；每層用不同顏色區分。穿透持股達 25% 的個人會標記為 UBO。',
      thisCompany: '本公司',
      thisCompanyTag: '本公司',
      nodePerson: '個人',
      nodeEntity: '實體',
      nodeNamePh: '股東名稱',
      uboBadge: 'UBO · 穿透 {{pct}}%',
      uboSummaryTitle: '最終受益人 (UBO)',
      uboSummaryHint: '所有穿透到的自然人；≥ {{threshold}}% 為 UBO（須 ID&V）',
      effPct: '穿透 {{pct}}%',
      uboUnnamed: '(未命名)',
      zoomIn: '放大',
      zoomOut: '縮小',
      zoomReset: '重置縮放',
      zoomFit: '自適應鋪滿',
      addEntityShareholder: '添加實體股東',
      addPersonShareholder: '添加個人股東',
      removeShareholder: '刪除該股東及其上游股東',
      directSum: '直接持股合計 {{sum}}%',
      directSumOk: ' ✓',
      directSumBad: '（應為 100%，無法提交）',
      noBoDeclare: '聲明無受益人（上市公司等可豁免穿透）',
      ownConfirm:
        '我確認以上股權 / 受益人結構真實、完整，並理解每位必核驗人需各自完成 ID&V 身份核驗。',
      // ── 關鍵人員 ──
      peopleTitle: '關鍵人員',
      peopleIntroA: '至少需各指定一名',
      peopleRoleBo: '受益人',
      peopleIntroB: '、',
      peopleRoleLr: '法定代表人',
      peopleIntroC: '、',
      peopleRoleFa: '檔案管理員',
      peopleIntroD:
        '（可由同一人兼任，也可分屬不同人）。持股達閾值（通常 25%）的受益人及全部董事 / 法定代表人 / 檔案管理員均須完成 ID&V 核驗。',
      peopleIntroEmail: '檔案管理員的信箱將作為本次申請的聯絡信箱。',
      personTag: '關鍵人員',
      fromTree: '來自股權結構 · 穿透 {{pct}}%',
      idvLabel: 'ID&V {{status}}',
      remove: '移除',
      firstName: '名',
      lastName: '姓',
      dob: '出生日期',
      nationality: '國籍',
      personEmail: '信箱（用於發 ID&V 邀請）',
      titleLabel: '職務',
      titlePh: 'CEO / 董事',
      ownershipPct: '持股百分比 (%)',
      roleBo: '受益人',
      roleDirector: '董事',
      roleLr: '法定代表人',
      roleFa: '檔案管理員',
      addOwner: '+ 添加受益人 / 管理員',
      // ── ID&V 徽章狀態 ──
      idvPassed: '已通過',
      idvFailed: '未通過',
      idvPending: '審核中',
      idvWaiting: '待核驗',
      // ── 操作區 ──
      submitHint: '提交前請在「股權結構」勾選結構確認；各 Tab 信息會一併提交。',
      saving: '保存中…',
      saveDraft: '保存草稿',
      submitting: '提交中…',
      submitApply: '提交申請並發起核驗',
      // ── 提交後清單 ──
      progressLabel: '核驗進度：',
      progressDoneSuffix: ' 項必核驗已通過',
      checklistTitle: 'ID&V 核驗清單',
      taskCompany: '企業主體',
      taskPerson: '相關人員',
      taskRequired: '必核驗',
      taskHolding: '持股 {{pct}}%',
      goVerifyShort: '去核驗',
      checklistFooter:
        '每位必核驗人各自打開自己的連結完成 ID&V；公司主體與全部必核驗人均通過後，帳戶將自動開立。',
      // ── 校驗 / 提示文案 ──
      errLegalName: '請填寫公司法定名稱',
      errRegNumber: '請填寫公司註冊號',
      errTaxId: '請填寫{{label}}（稅務號必填）',
      errIncState: '請填寫註冊州 / 省',
      errIncDate: '請填寫註冊日期',
      errRegAddress: '請完整填寫註冊地址（地址行 1 / 城市 / 州省 / 郵編）',
      errFileAdmin: '請指定一名檔案管理員',
      errFileAdminEmail: '檔案管理員需填寫有效信箱（將作為聯絡信箱）',
      errLegalRep: '請指定法定代表人',
      errBeneficialOwner: '請指定受益人，或勾選「無受益人」聲明',
      errOwnershipSum: '「{{name}}」的直接持股合計為 {{sum}}%，應為 100%',
      errOwnershipConfirm: '請勾選確認股權/受益人結構屬實後再提交',
      draftEntityPrefix: '保存草稿前請先填好「實體信息」：',
      noticeDraftSaved: '草稿已保存',
      errDraftFailed: '保存草稿失敗：',
      errSubmitFailed: '提交失敗：',
      errStatusFailed: '查詢狀態失敗：',
      msgFallback: '請求失敗，請稍後再試',
      // ── 稅務號標籤 ──
      taxFallback: '稅務 / 登記號',
      tax_US: 'EIN（僱主識別號）',
      tax_CN: '統一社會信用代碼',
      tax_DE: '稅號',
      tax_GB: '公司註冊號',
      tax_FR: '企業識別號（SIREN）',
      tax_AU: '商業號碼（ABN）',
      tax_SG: '實體編號（UEN）',
      tax_HK: '商業登記號（BRN）',
      tax_JP: '法人番號',
      tax_CA: '商業號碼（BN）',
      // ── 證明文件分頁 ──
      tabDocuments: '證明文件',
      docsCompanyTitle: '公司證明文件',
      docsCompanyHint:
        '上傳公司證明文件。必填項目須提供；條件項目僅在適用時（如加強盡職調查）才需提交。',
      docsPersonTitle: '人員證明文件',
      docsPersonHint: '為每位關鍵人員（UBO／董事／法定代表人／檔案管理員）上傳身分與地址證明。',
      docsNoPersons: '尚未保存任何人員。請先保存申請（保存草稿），再於此處上傳人員文件。',
      docRequired: '必填',
      docConditional: '按需',
      docUpload: '上傳',
      docUploading: '上傳中…',
      docDelete: '刪除',
      docNeedApp: '請先保存申請（保存草稿），再上傳證明文件。',
      docUploadedOk: '已上傳 {{name}}',
      docUploadFailed: '上傳失敗：',
      docDeleteFailed: '刪除失敗：',
      docState_required: '待上傳',
      docState_uploaded: '已上傳',
      docState_under_review: '審核中',
      docState_verified: '已核驗',
      docState_rejected: '已退回',
      docState_expired: '已過期',
      docTypes: {
        certificate_of_incorporation: '公司註冊證書',
        articles_of_association: '公司章程',
        proof_of_registered_address: '註冊地址證明（近 3 個月）',
        ubo_declaration: '最終受益人（UBO）聲明',
        business_license: '營業執照',
        bank_statement: '銀行對帳單（近 3 個月）',
        financial_statements: '財務報表（加強盡調 EDD）',
        certificate_of_good_standing: '良好信譽證明',
        board_resolution: '董事會決議',
        tax_registration: '稅務登記證',
        register_of_directors: '董事名冊',
        register_of_shareholders: '股東名冊',
        passport_or_national_id: '護照 / 身分證',
        proof_of_address: '個人地址證明（近 3 個月）',
        power_of_attorney: '授權委託書',
        source_of_wealth_evidence: '財富來源證明（加強盡調 EDD）',
        nnc1_incorporation_form: '法團成立表格 NNC1（首任董事＋創辦成員）',
        nar1_annual_return: '周年申報表 NAR1（董事＋股東＋股本）',
        significant_controllers_register: '重要控制人登記冊 SCR（UBO）',
        acra_business_profile: 'ACRA Business Profile（董事＋股東＋股本）',
        asic_company_extract: 'ASIC Current Company Extract（董事＋股東）',
        confirmation_statement: '周年確認表 CS01（股東＋董事＋股本）',
        psc_register: '重要控制人登記冊 PSC',
        gesellschafterliste: '股東名單 Gesellschafterliste',
        handelsregisterauszug: '商業登記摘錄 Handelsregisterauszug（董事/經理）',
        b1_annual_return: '周年申報表 Form B1（董事＋股東）',
        company_extract: '公司登記摘錄 Company Extract（董事＋股東）',
        particulars_of_shareholding: '股權明細 Particulars of Shareholding',
        extrait_kbis: '商業登記摘錄 Extrait Kbis（法定代表人/董事）',
        statuts_articles_of_association: '公司章程 Statuts（含股東/合夥人）',
        beneficial_owners_declaration: '受益人登記 RBE（UBO）',
        kvk_business_register_extract: '商會登記摘錄 KvK Uittreksel（董事）',
        shareholder_register: '股東名冊 Aandeelhoudersregister',
        ubo_register_extract: 'UBO 登記摘錄',
        commercial_register_extract: '商業登記摘錄（董事）',
        share_register_aktienregister: '股東名冊 Aktienregister（股份公司）',
        commercial_register_extract_gmbh_members: '商業登記摘錄（GmbH 股東/股權）',
        registro_mercantil_extract: '商業登記摘錄 Nota Simple（董事）',
        good_standing_certificate: '良好信譽證明',
        escritura_de_constitucion: '設立公證書 Escritura（含股東）',
        beneficial_ownership_certificate: '受益人證明 Certificado de Titularidad Real',
        visura_camerale: '公司登記摘錄 Visura Camerale（董事＋股東）',
        certificate_of_all_historical_matters: '履歷事項全部證明書（董事）',
        shareholder_registry_certificate: '股主名簿記載事項證明書（股東）',
        memorandum_of_association: '公司組織章程大綱 MOA（股東）',
        share_certificate: '股份證書 / 股東名冊',
        certificate_of_incumbency: '在任證明 Certificate of Incumbency（董事＋股東）',
        board_resolution_appointment: '董事委任決議 Board Resolution',
        cap_table: '股權結構表 Cap Table',
        stock_ledger: '股票過戶登記冊 Stock Ledger',
        operating_agreement: 'LLC 經營協議 Operating Agreement（成員/股權）',
        statement_of_information: '公司資訊聲明 Statement of Information（董事/高管）',
        corporate_bylaws: '公司細則 Bylaws / 董事會決議',
        corporate_profile_report: '公司檔案報告 Corporate Profile Report（董事）',
        isc_register: '重要控制人登記冊 ISC（UBO）',
      },
    },
    security: {
      title: '帳戶安全',
      subtitle: '綁定兩步驗證或 Passkey，保護你的帳戶與交易安全。',
      enrollPrompt1: '為保障交易安全，請至少綁定 ',
      enrollPrompt2: ' 之一後再繼續使用。',
      totp: '兩步驗證（TOTP）',
      totpDesc: '使用 Google Authenticator / Authy 等認證器 App。',
      bindTotp: '綁定動態驗證碼',
      disableTotp: '關閉兩步驗證',
      scanStep: '① 用認證器 App 掃碼，或手動輸入金鑰：',
      confirmStep: '② 輸入 App 顯示的 6 位動態碼確認：',
      confirmBind: '確認綁定',
      recoveryIntro: '✅ 已啟用。請妥善保存以下一次性恢復碼（認證器遺失時使用，僅顯示這一次）：',
      savedRecovery: '我已保存',
      passkey: '通行金鑰',
      passkeyDesc: '用裝置生物辨識／安全金鑰免密登入，本身即強二次因子。',
      addPasskey: '新增 Passkey',
      noPasskey: '尚未新增 Passkey。',
      currentCode: '目前驗證碼／恢復碼',
      totpCode: '6 位動態碼',
      deviceAlias: '裝置別名（如：我的手機）',
      enabled: '已啟用',
      disabled: '未啟用',
      or: '或',
      totpCodeName: 'TOTP 動態驗證碼',
      unnamedDevice: '未命名裝置',
      addedOn: '新增於',
      delete: '刪除',
    },
    apikeys: {
      title: 'API 金鑰',
      subtitle:
        '管理用於伺服器端呼叫的存取金鑰（AK/SK）。金鑰經 customer → auth 簽發，gateway 用其驗簽放行請求。',
      create: '建立 API 金鑰',
      all: '全部',
      active: '啟用',
      revoked: '已撤銷',
      owner: '擁有者',
      application: 'application',
      merchant: 'merchant',
      refresh: '重新整理',
      empty: '還沒有 API 金鑰',
      createFirst: '建立第一個 API 金鑰',
      name: '名稱',
      accessKey: 'Access Key',
      scopes: 'Scopes',
      createdAt: '建立時間',
      lastUsed: '最近使用',
      expiry: '到期',
      none: '無',
      copied: '已複製',
      revoke: '撤銷',
      scopesLabel: '權限 Scopes',
      validity: '有效期',
      secretOnce: '⚠️ Secret Key 僅在此處顯示一次，關閉後無法再查看，請立即妥善保存。',
      ak: 'Access Key (AK)',
      sk: 'Secret Key (SK)',
      savedIt: '我已保存',
      revokeTitle: '撤銷 API 金鑰',
      revokeConfirm1: '確認撤銷「',
      revokeConfirm2: '此操作不可逆，使用該金鑰的所有呼叫將立即失敗。',
      confirmRevoke: '確認撤銷',
      namePlaceholder: '如 gateway-prod',
      copy: '複製',
      owns: '歸屬',
      creating: '建立中…',
      createShort: '建立',
      neverUsed: '從未',
      neverExpire: '永不',
      createdSuccess: '「{{name}}」建立成功',
    },
  },
};

// 静态加载器：直接返回内存字典（避免依赖 HTTP/资源服务，纯 SPA 静态托管也稳）。
@Injectable()
export class StaticTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<Record<string, any>> {
    return of(TRANSLATIONS[lang as LangCode] ?? TRANSLATIONS[DEFAULT_LANG]);
  }
}

// detectBrowserLang 按浏览器语言推断默认语言（仅提供 en/de/zh-TW，中文一律繁体）。SSR 无 navigator 时回默认。
export function detectBrowserLang(): LangCode {
  if (typeof navigator === 'undefined') return DEFAULT_LANG;
  const list = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const raw of list) {
    const l = (raw || '').toLowerCase();
    if (l.startsWith('de')) return 'de';
    if (l.startsWith('zh')) return 'zh-TW';
    if (l.startsWith('en')) return 'en';
  }
  return DEFAULT_LANG;
}

// 读取持久化语言（localStorage）；无存储时一律默认英文（规则：所有界面首个版本以英文出现，
// 不按浏览器语言探测，用户显式切换后才持久化其它语言）。SSR 安全（无 window 时返回默认）。
export function storedLang(): LangCode {
  if (typeof localStorage === 'undefined') return DEFAULT_LANG;
  const v = localStorage.getItem(LS_KEY) as LangCode | null;
  if (LANGS.some((l) => l.code === v)) return v as LangCode;
  return DEFAULT_LANG;
}

export function setLang(translate: TranslateService, lang: LangCode): void {
  translate.use(lang);
  if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, lang);
}

// ── i18n 迁移追加(2026-07-16 工作流生成);deep-merge 不覆盖现有键 ──
const __MIGRATED: Record<string, Record<string, any>> = {
  en: {
    pgApikeys: {
      loadFailed: 'Query failed: {{msg}}',
      nameRequired: 'Please enter a name',
      createFailed: 'Failed to issue key: {{msg}}',
      revokeFailed: 'Revocation failed: {{msg}}',
      colon: ': ',
      revokeConfirmSuffix: '"?',
    },
    pgDashboard: {
      operationFailed: 'Operation failed',
    },
    pgLogs: {
      loadFailed: 'Failed to load',
    },
    pgProfile: {
      ownershipChart: 'Ownership Structure',
      applicantEntity: 'Applicant Entity',
      legalShareholder: 'Corporate Shareholder',
      naturalPerson: 'Individual',
      previewLoadError: 'Failed to load file or access denied',
      clickToZoom: 'Click to enlarge',
      download: 'Download',
      previewUnsupported: 'Preview not available for this type',
      noFileUploaded: 'No file uploaded yet',
      previewUnsupportedType: "Preview isn't available for this file type ({{type}}). ",
      clickToDownload: 'Click to download',
      file: 'File',
    },
    pgRegister: {
      errPasswordTooShort: 'Password must be at least 8 characters',
      errPasswordMismatch: "The passwords don't match",
      errRegisterFailed: 'Registration failed. Please try again.',
    },
    pgTransactions: {
      viewBalance: 'Balance activity',
      viewOrders: 'Orders',
      status: 'Status',
      orderRef: 'Order ref',
      relatedOrder: 'Original order',
      actions: 'Actions',
      refund: 'Refund',
      refundConfirm: 'Refund this payment back to the payer?',
      refundFailed: 'Refund failed. Please try again later.',
      refundUnsupported:
        'This order cannot be refunded directly by the payment provider. You can send the money back to the payer using Transfer.',
      goTransfer: 'Go to Transfer',
      mvType: {
        collection: 'Incoming payment',
        deposit: 'Top-up',
        withdraw: 'Withdrawal',
        transfer: 'Transfer',
        payout: 'Payout',
        refund: 'Refund',
        unknown: '—',
      },
      mvStatus: {
        pending: 'Awaiting provider',
        held: 'Under review',
        completed: 'Completed',
        refunding: 'Refund in progress',
        refunded: 'Refunded',
        refund_pending_ops: 'Manual refund pending',
        failed: 'Failed',
        unknown: '—',
      },
      searchPlaceholder: 'Search account / type',
      allTypes: 'All types',
      allCurrencies: 'All currencies',
      allAccounts: 'All accounts',
      date: 'Date',
      clear: 'Clear',
      type: 'Type',
      direction: 'Direction',
      currency: 'Currency',
      amount: 'Amount',
      fee: 'Fee',
      afterBalance: 'Balance after',
      noMatch: 'No matching transactions',
      paginationSummary: '{{total}} total · showing {{start}}–{{end}}',
      perPage: 'Per page',
      dirIn: 'Incoming',
      dirOut: 'Outgoing',
      dirExchange: 'Exchange',
    },
    pgMisc: {
      oauthCallbackParamsMissing: 'OAuth callback parameters are missing',
      oauthLoginFailed: 'OAuth sign-in failed',
      loginNoToken: 'The login response did not include a token',
      myDevice: 'My device',
      operationFailed: 'Operation failed',
    },
    pgAccounts: {
      accountNo: 'Account No.',
      accountOpened: '{{ currency }} receiving account activated',
      accountOpening:
        "Your account is being set up; the bank details will appear once it's active.",
      afterOpenNote:
        'Once the account is open, the bank name, account number and SWIFT are returned by the provider and shown here.',
      applyOpenBtn: 'Apply to open',
      applyReceivingAccount: 'Apply for a receiving account',
      autoOpenAfterReview:
        'Once approved, the receiving account opens automatically—no need to apply again.',
      balanceNote:
        'Stablecoins counted 1:1; fiat converted at FX (coming soon) · {{ count }} currencies',
      bank: 'Bank',
      bankAddr: 'Bank address',
      bankCode: 'Bank code',
      beneficiary: 'Beneficiary',
      beneficiaryAddr: 'Beneficiary address',
      cancel: 'Cancel',
      channelRequiresMaterials: 'The provider needs additional documents',
      channelReviewing: 'Provider review in progress',
      chooseFile: 'Choose file',
      country: 'Country',
      docApprovedNote: 'Funds are credited only after the documents are approved.',
      docBankReceipt: 'Bank receipt',
      docMissingHint: 'Still required:',
      docContract: 'Contract',
      docOrders: 'Orders',
      docOther: 'Other documents',
      docRequired: 'A payment needs trade documents submitted',
      docReturnedResubmit: 'Documents were returned—please resubmit',
      docShipping: 'Shipping document',
      docSubmittedReview:
        'The documents for order {{ orderId }} have been submitted and are under provider review.',
      docWithdrawalCert: 'Withdrawal proof',
      done: 'Done',
      fiat: 'Fiat',
      fiatBanks: 'Fiat receiving banks',
      goProfileUpload: 'Go to company profile to upload',
      loadingCurrencies: 'Loading available currencies…',
      materialsRequiredTitle: 'Additional documents required',
      materialsSubmittedReview:
        'Documents submitted and under provider review—once approved, your receiving account opens automatically.',
      nBanks: '{{ count }} banks',
      nCurrencies: '{{ count }} currencies',
      needMoreMaterialsToOpen: 'More documents are needed to open your receiving account',
      noBanks: 'No receiving banks yet',
      noCurrenciesAvailable: 'No currencies are available to open right now.',
      noWallets: 'No stablecoin receiving addresses yet',
      nWallets: '{{ count }}',
      orderLine: 'Order {{ orderId }} · {{ type }}',
      processing: 'Processing…',
      railAch: 'ACH / Fedwire · US domestic',
      railArCbu: 'CBU · Argentina domestic',
      railAuPayid: 'PayID/Osko · Australia instant',
      railBrPix: 'PIX · Brazil instant',
      railCnCnaps: 'CNAPS · RMB clearing',
      railEgIpn: 'IPN · Egypt instant',
      railFpsUk: 'Faster Payments · UK domestic',
      railHkChats: 'CHATS · HKD settlement',
      railHkFps: 'FPS · Hong Kong instant',
      railInImps: 'IMPS/UPI · India instant',
      railJpZengin: 'Zengin · Japan domestic',
      railKePesalink: 'PesaLink · Kenya domestic',
      railMxSpei: 'SPEI · Mexico domestic',
      railNgNip: 'NIP · Nigeria instant (NUBAN)',
      railSepa: 'SEPA · Eurozone',
      railSgFast: 'FAST · Singapore instant',
      railSwift: 'SWIFT · International wire',
      railZaEft: 'EFT · South Africa domestic',
      rejectReason: 'Reason for return: {{ remark }}',
      selectCurrencyHint:
        'Choose the currencies you want to receive in. Once enabled, the platform automatically assigns the matching receiving bank account.',
      settlementPlusBalances: 'Settlement account + balances by currency',
      stablecoin: 'Stablecoin',
      stablecoinWallets: 'Stablecoin receiving addresses',
      status: 'Status',
      submitDoc: 'Submit documents',
      submitFailed: 'Submission failed',
      submitTradeDoc: 'Submit trade documents',
      totalBalanceUsd: 'Total balance · in USD',
      uploadFailed: 'Upload failed',
    },
  },
  de: {
    pgApikeys: {
      loadFailed: 'Abfrage fehlgeschlagen: {{msg}}',
      nameRequired: 'Bitte geben Sie einen Namen ein',
      createFailed: 'Schlüsselausstellung fehlgeschlagen: {{msg}}',
      revokeFailed: 'Widerruf fehlgeschlagen: {{msg}}',
      colon: ': ',
      revokeConfirmSuffix: '"?',
    },
    pgDashboard: {
      operationFailed: 'Vorgang fehlgeschlagen',
    },
    pgLogs: {
      loadFailed: 'Laden fehlgeschlagen',
    },
    pgProfile: {
      ownershipChart: 'Beteiligungsstruktur',
      applicantEntity: 'Antragstellendes Unternehmen',
      legalShareholder: 'Juristischer Gesellschafter',
      naturalPerson: 'Natürliche Person',
      previewLoadError: 'Datei konnte nicht geladen werden oder kein Zugriff',
      clickToZoom: 'Zum Vergrößern klicken',
      download: 'Herunterladen',
      previewUnsupported: 'Für diesen Typ ist keine Vorschau verfügbar',
      noFileUploaded: 'Noch keine Datei hochgeladen',
      previewUnsupportedType: 'Für diesen Dateityp ({{type}}) ist keine Vorschau verfügbar. ',
      clickToDownload: 'Zum Herunterladen klicken',
      file: 'Datei',
    },
    pgRegister: {
      errPasswordTooShort: 'Das Passwort muss mindestens 8 Zeichen lang sein',
      errPasswordMismatch: 'Die Passwörter stimmen nicht überein',
      errRegisterFailed: 'Registrierung fehlgeschlagen. Bitte versuche es erneut.',
    },
    pgTransactions: {
      viewBalance: 'Kontobewegungen',
      viewOrders: 'Aufträge',
      status: 'Status',
      orderRef: 'Auftragsnr.',
      relatedOrder: 'Ursprünglicher Auftrag',
      actions: 'Aktionen',
      refund: 'Erstatten',
      refundConfirm: 'Diese Zahlung an den Zahler zurückerstatten?',
      refundFailed: 'Erstattung fehlgeschlagen. Bitte später erneut versuchen.',
      refundUnsupported:
        'Dieser Auftrag kann vom Zahlungsdienstleister nicht direkt erstattet werden. Sie können das Geld per Überweisung an den Zahler zurücksenden.',
      goTransfer: 'Zur Überweisung',
      mvType: {
        collection: 'Zahlungseingang',
        deposit: 'Aufladung',
        withdraw: 'Auszahlung',
        transfer: 'Überweisung',
        payout: 'Zahlung',
        refund: 'Erstattung',
        unknown: '—',
      },
      mvStatus: {
        pending: 'Warten auf Anbieter',
        held: 'In Prüfung',
        completed: 'Abgeschlossen',
        refunding: 'Erstattung läuft',
        refunded: 'Erstattet',
        refund_pending_ops: 'Manuelle Erstattung ausstehend',
        failed: 'Fehlgeschlagen',
        unknown: '—',
      },
      searchPlaceholder: 'Konto / Typ suchen',
      allTypes: 'Alle Typen',
      allCurrencies: 'Alle Währungen',
      allAccounts: 'Alle Konten',
      date: 'Datum',
      clear: 'Zurücksetzen',
      type: 'Typ',
      direction: 'Richtung',
      currency: 'Währung',
      amount: 'Betrag',
      fee: 'Gebühr',
      afterBalance: 'Saldo danach',
      noMatch: 'Keine passenden Transaktionen',
      paginationSummary: '{{total}} gesamt · Anzeige {{start}}–{{end}}',
      perPage: 'Pro Seite',
      dirIn: 'Eingang',
      dirOut: 'Ausgang',
      dirExchange: 'Umtausch',
    },
    pgMisc: {
      oauthCallbackParamsMissing: 'OAuth-Callback-Parameter fehlen',
      oauthLoginFailed: 'OAuth-Anmeldung fehlgeschlagen',
      loginNoToken: 'Die Anmeldeantwort enthielt kein Token',
      myDevice: 'Mein Gerät',
      operationFailed: 'Vorgang fehlgeschlagen',
    },
    pgAccounts: {
      accountNo: 'Kontonummer',
      accountOpened: '{{ currency }}-Empfangskonto aktiviert',
      accountOpening: 'Ihr Konto wird eingerichtet; die Bankdaten erscheinen, sobald es aktiv ist.',
      afterOpenNote:
        'Sobald das Konto eröffnet ist, werden Bankname, Kontonummer und SWIFT vom Anbieter zurückgegeben und hier angezeigt.',
      applyOpenBtn: 'Freischaltung beantragen',
      applyReceivingAccount: 'Empfangskonto beantragen',
      autoOpenAfterReview:
        'Nach der Genehmigung wird das Empfangskonto automatisch eröffnet – ein erneuter Antrag ist nicht nötig.',
      balanceNote:
        'Stablecoins 1:1 gerechnet; Fiat zum FX-Kurs umgerechnet (in Kürze) · {{ count }} Währungen',
      bank: 'Bank',
      bankAddr: 'Bankadresse',
      bankCode: 'Bankleitzahl',
      beneficiary: 'Begünstigter',
      beneficiaryAddr: 'Adresse des Begünstigten',
      cancel: 'Abbrechen',
      channelRequiresMaterials: 'Der Anbieter benötigt zusätzliche Unterlagen',
      channelReviewing: 'Prüfung durch den Anbieter läuft',
      chooseFile: 'Datei auswählen',
      country: 'Land',
      docApprovedNote: 'Der Betrag wird erst nach Genehmigung der Unterlagen gutgeschrieben.',
      docBankReceipt: 'Bankbeleg',
      docMissingHint: 'Noch erforderlich:',
      docContract: 'Vertrag',
      docOrders: 'Bestellungen',
      docOther: 'Sonstige Unterlagen',
      docRequired: 'Für eine Zahlung müssen Handelsdokumente eingereicht werden',
      docReturnedResubmit: 'Die Unterlagen wurden zurückgeschickt – bitte erneut einreichen',
      docShipping: 'Versandbeleg',
      docSubmittedReview:
        'Die Unterlagen für Bestellung {{ orderId }} wurden eingereicht und werden vom Anbieter geprüft.',
      docWithdrawalCert: 'Auszahlungsnachweis',
      done: 'Fertig',
      fiat: 'Fiat',
      fiatBanks: 'Fiat-Empfangsbanken',
      goProfileUpload: 'Im Unternehmensprofil hochladen',
      loadingCurrencies: 'Verfügbare Währungen werden geladen…',
      materialsRequiredTitle: 'Zusätzliche Unterlagen erforderlich',
      materialsSubmittedReview:
        'Unterlagen eingereicht und in Prüfung durch den Anbieter – nach der Genehmigung wird Ihr Empfangskonto automatisch eröffnet.',
      nBanks: '{{ count }} Banken',
      nCurrencies: '{{ count }} Währungen',
      needMoreMaterialsToOpen:
        'Zur Eröffnung des Empfangskontos sind weitere Unterlagen erforderlich',
      noBanks: 'Noch keine Empfangsbanken',
      noCurrenciesAvailable: 'Derzeit sind keine Währungen zur Freischaltung verfügbar.',
      noWallets: 'Noch keine Stablecoin-Empfangsadressen',
      nWallets: '{{ count }}',
      orderLine: 'Bestellung {{ orderId }} · {{ type }}',
      processing: 'Wird verarbeitet…',
      railAch: 'ACH / Fedwire · USA (Inland)',
      railArCbu: 'CBU · Argentinien (Inland)',
      railAuPayid: 'PayID/Osko · Australien (Echtzeit)',
      railBrPix: 'PIX · Brasilien (Echtzeit)',
      railCnCnaps: 'CNAPS · RMB-Clearing',
      railEgIpn: 'IPN · Ägypten (Echtzeit)',
      railFpsUk: 'Faster Payments · UK (Inland)',
      railHkChats: 'CHATS · HKD-Abwicklung',
      railHkFps: 'FPS · Hongkong (Echtzeit)',
      railInImps: 'IMPS/UPI · Indien (Echtzeit)',
      railJpZengin: 'Zengin · Japan (Inland)',
      railKePesalink: 'PesaLink · Kenia (Inland)',
      railMxSpei: 'SPEI · Mexiko (Inland)',
      railNgNip: 'NIP · Nigeria (Echtzeit, NUBAN)',
      railSepa: 'SEPA · Eurozone',
      railSgFast: 'FAST · Singapur (Echtzeit)',
      railSwift: 'SWIFT · Internationale Überweisung',
      railZaEft: 'EFT · Südafrika (Inland)',
      rejectReason: 'Grund für die Rücksendung: {{ remark }}',
      selectCurrencyHint:
        'Wählen Sie die Währungen, in denen Sie Zahlungen empfangen möchten. Nach der Freischaltung weist die Plattform automatisch das passende Empfangsbankkonto zu.',
      settlementPlusBalances: 'Verrechnungskonto + Salden je Währung',
      stablecoin: 'Stablecoin',
      stablecoinWallets: 'Stablecoin-Empfangsadressen',
      status: 'Status',
      submitDoc: 'Unterlagen einreichen',
      submitFailed: 'Einreichung fehlgeschlagen',
      submitTradeDoc: 'Handelsdokumente einreichen',
      totalBalanceUsd: 'Gesamtsaldo · in USD',
      uploadFailed: 'Upload fehlgeschlagen',
    },
  },
  'zh-TW': {
    pgApikeys: {
      loadFailed: '查詢失敗：{{msg}}',
      nameRequired: '請填寫名稱',
      createFailed: '下發失敗：{{msg}}',
      revokeFailed: '吊銷失敗：{{msg}}',
      colon: '：',
      revokeConfirmSuffix: '」？',
    },
    pgDashboard: {
      operationFailed: '操作失敗',
    },
    pgLogs: {
      loadFailed: '加载失败',
    },
    pgProfile: {
      ownershipChart: '股權架構圖',
      applicantEntity: '申請主體',
      legalShareholder: '法人股東',
      naturalPerson: '自然人',
      previewLoadError: '檔案載入失敗或無權存取',
      clickToZoom: '點擊放大',
      download: '下載',
      previewUnsupported: '該類型暫不支援預覽',
      noFileUploaded: '尚未上傳檔案',
      previewUnsupportedType: '此檔案類型（{{type}}）暫不支援預覽。',
      clickToDownload: '點擊下載',
      file: '檔案',
    },
    pgRegister: {
      errPasswordTooShort: '密码至少 8 位',
      errPasswordMismatch: '两次输入的密码不一致',
      errRegisterFailed: '注册失败，请重试',
    },
    pgTransactions: {
      viewBalance: '餘額流水',
      viewOrders: '訂單',
      status: '狀態',
      orderRef: '訂單號',
      relatedOrder: '關聯原單',
      actions: '操作',
      refund: '退款',
      refundConfirm: '確定將這筆款項退回付款方？',
      refundFailed: '退款失敗，請稍後再試。',
      refundUnsupported: '此訂單無法由通道方直接退款。您可以透過【轉帳】把款項退回付款方。',
      goTransfer: '前往轉帳',
      mvType: {
        collection: '收款',
        deposit: '充值',
        withdraw: '提現',
        transfer: '轉帳',
        payout: '付款',
        refund: '退款',
        unknown: '—',
      },
      mvStatus: {
        pending: '等待通道方',
        held: '審核中',
        completed: '已完成',
        refunding: '退款處理中',
        refunded: '已退款',
        refund_pending_ops: '待人工退款',
        failed: '失敗',
        unknown: '—',
      },
      searchPlaceholder: '搜尋帳戶 / 類型',
      allTypes: '全部類型',
      allCurrencies: '全部幣種',
      allAccounts: '全部帳戶',
      date: '日期',
      clear: '清除',
      type: '類型',
      direction: '方向',
      currency: '幣種',
      amount: '金額',
      fee: '手續費',
      afterBalance: '執行後餘額',
      noMatch: '無匹配的交易',
      paginationSummary: '共 {{total}} 筆 · 顯示 {{start}}–{{end}}',
      perPage: '每頁',
      dirIn: '收入',
      dirOut: '支出',
      dirExchange: '兌換',
    },
    pgMisc: {
      oauthCallbackParamsMissing: 'OAuth 回調參數缺失',
      oauthLoginFailed: 'OAuth 登入失敗',
      loginNoToken: '登入回應中沒有 token',
      myDevice: '我的裝置',
      operationFailed: '操作失敗',
    },
    pgAccounts: {
      accountNo: '帳號',
      accountOpened: '{{ currency }} 收款帳戶已開通',
      accountOpening: '帳戶正在開通中，銀行資訊會在生效後補齊。',
      afterOpenNote: '帳戶開通後，銀行名稱、帳號與 SWIFT 由通道商返回並顯示在此。',
      applyOpenBtn: '申请开通',
      applyReceivingAccount: '申請收款帳戶',
      autoOpenAfterReview: '審核通過後會自動開立收款帳戶，無需再次申請。',
      balanceNote: '稳定币按 1:1 计入；法币按 FX 折算（待接）· {{ count }} 个币种',
      bank: '銀行',
      bankAddr: '银行地址',
      bankCode: '银行代码',
      beneficiary: '收款人',
      beneficiaryAddr: '收款人地址',
      cancel: '取消',
      channelRequiresMaterials: '通道方要求補充資料',
      channelReviewing: '通道方審核中',
      chooseFile: '選擇檔案',
      country: '国家',
      docApprovedNote: '單據審核通過後，款項才會入帳。',
      docBankReceipt: '銀行回單',
      docMissingHint: '尚未提供：',
      docContract: '合同',
      docOrders: '訂單',
      docOther: '其他材料',
      docRequired: '有一筆收款需要補交貿易單據',
      docReturnedResubmit: '單據被退回，需重新提交',
      docShipping: '物流單',
      docSubmittedReview: '訂單 {{ orderId }} 的單據已提交，通道方審核中。',
      docWithdrawalCert: '提現證明',
      done: '完成',
      fiat: '法币',
      fiatBanks: '法币收款银行',
      goProfileUpload: '前往企業檔案補傳',
      loadingCurrencies: '載入可開通幣種…',
      materialsRequiredTitle: '需要補充資料',
      materialsSubmittedReview: '資料已提交，通道方審核中 —— 審核通過後會自動開立收款帳戶。',
      nBanks: '{{ count }} 家',
      nCurrencies: '{{ count }} 币种',
      needMoreMaterialsToOpen: '開通收款帳戶還需補充資料',
      noBanks: '暂无已开通收款银行',
      noCurrenciesAvailable: '目前沒有可開通的幣種。',
      noWallets: '暂无稳定币收款地址',
      nWallets: '{{ count }} 个',
      orderLine: '訂單 {{ orderId }} · {{ type }}',
      processing: '處理中…',
      railAch: 'ACH / Fedwire · 美国本地',
      railArCbu: 'CBU · 阿根廷本地',
      railAuPayid: 'PayID/Osko · 澳洲即时',
      railBrPix: 'PIX · 巴西即时',
      railCnCnaps: 'CNAPS · 人民币清算',
      railEgIpn: 'IPN · 埃及即时',
      railFpsUk: 'Faster Payments · 英国本地',
      railHkChats: 'CHATS · 港元结算',
      railHkFps: 'FPS · 转数快',
      railInImps: 'IMPS/UPI · 印度即时',
      railJpZengin: '全银系统 · 日本本地',
      railKePesalink: 'PesaLink · 肯尼亚本地',
      railMxSpei: 'SPEI · 墨西哥本地',
      railNgNip: 'NIP · 尼日利亚即时（NUBAN）',
      railSepa: 'SEPA · 欧元区',
      railSgFast: 'FAST · 新加坡即时',
      railSwift: 'SWIFT · 国际电汇',
      railZaEft: 'EFT · 南非本地',
      rejectReason: '退回原因：{{ remark }}',
      selectCurrencyHint: '選擇要開通的收款幣種。開通後由平台自動分配對應的收款銀行帳戶。',
      settlementPlusBalances: '结算账户 + 各币种余额',
      stablecoin: '稳定币',
      stablecoinWallets: '稳定币收款地址',
      status: '狀態',
      submitDoc: '提交單據',
      submitFailed: '提交失敗',
      submitTradeDoc: '提交貿易單據',
      totalBalanceUsd: '总余额 · 折合 USD',
      uploadFailed: '上傳失敗',
    },
  },
};
(function () {
  const dm = (a: any, b: any): any => {
    for (const k in b) {
      a[k] =
        a[k] && typeof a[k] === 'object' && !Array.isArray(a[k]) && b[k] && typeof b[k] === 'object'
          ? dm(a[k], b[k])
          : b[k];
    }
    return a;
  };
  for (const lang of Object.keys(__MIGRATED)) dm((TRANSLATIONS as any)[lang], __MIGRATED[lang]);
})();
